import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  XMarkIcon,
  PlayIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../../services/apiService";
import ProjectFolderSelectModal from "./ProjectFolderSelectModal";
import {
  ActivityDefinition,
  Country,
  ProjectFormElement,
  Workflow,
  WorkflowFormValueSuggestion,
  NodeType,
} from "../../types";
import toast from "../../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { normalizeCurrencyCode } from "../../utils/currency";
import {
  getProvinces,
  getDistricts,
  getNeighborhoods,
  TURKEY_LOCATIONS,
} from "../../data/turkey-locations";

interface WorkflowStartModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  workflowId: number;
  onSuccess?: () => void;
  isEditMode?: boolean;
  isTemplateChange?: boolean;
  initialData?: Record<string, any>;
  workflowInstanceId?: number; // For edit mode: load/save data directly from workflow instance
}

interface PaftaGroup {
  startNodeId: string;
  label: string;
  description?: string;
  nodeIds: string[];
  activityIds: number[];
}

type ProjectUploadFile = {
  id: string;
  file: LocalProjectUploadFile;
  description: string;
  folderId: string | null;
  folderPath: string;
  folderPathEntries: FolderPathEntry[];
};

type LocalProjectUploadFile = File & {
  webkitRelativePath?: string;
};

type FolderPathEntry = {
  id: string | null;
  name: string;
};

type QueuedElementUploadFile = {
  id: string;
  file: File;
  description: string;
};

const NAV_REFRESH_EVENT = "nav:refresh";
const INVOICE_YES = "yes";
const INVOICE_NO = "no";
const INVOICE_SUBFOLDER = "Faturalar";
const INVOICE_CATEGORY = "invoice";
const OFFER_SUBFOLDER = "İş Tutarı - Teklifler";
const OFFER_CATEGORY = "offer_amount";
const PROJECT_CREATION_UPLOAD_CATEGORY = "project_creation";
const PROJECT_CURRENCY_OPTIONS = [
  { code: "TRY", symbol: "\u20BA", name: "Turkish Lira" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "\u20AC", name: "Euro" },
  { code: "GBP", symbol: "\u00A3", name: "British Pound" },
  { code: "AZN", symbol: "\u20BC", name: "Azerbaijani Manat" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
  { code: "JPY", symbol: "\u00A5", name: "Japanese Yen" },
  { code: "CNY", symbol: "\u00A5", name: "Chinese Yuan" },
  { code: "RUB", symbol: "\u20BD", name: "Russian Ruble" },
  { code: "SAR", symbol: "\uFDFC", name: "Saudi Riyal" },
  { code: "AED", symbol: "\u062F.\u0625", name: "UAE Dirham" },
] as const;
const PROJECT_CURRENCY_SYMBOLS = PROJECT_CURRENCY_OPTIONS.reduce<
  Record<string, string>
>((acc, option) => {
  acc[option.code] = option.symbol;
  return acc;
}, {});
const MONEY_ELEMENT_TYPES = new Set(["budget_input", "currency_input", OFFER_CATEGORY]);
const FINANCIAL_ELEMENT_TYPES = new Set([
  "budget_input",
  "currency_input",
  OFFER_CATEGORY,
  "warning_threshold",
  "notification_roles",
  "cost_calculator",
  INVOICE_CATEGORY,
]);
const FINANCIAL_FIELD_ID_HINTS = new Set([
  "currency",
  "currencycode",
  "projectcurrency",
  "para_birimi",
  "parabirimi",
  "project_currency",
]);
const FINANCIAL_LABEL_HINTS = ["para birimi", "doviz", "currency"] as const;

const normalizeFinancialToken = (value: unknown): string =>
  String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9_ ]/g, "");

const isCurrencySensitiveElement = (element: ProjectFormElement): boolean => {
  const normalizedId = normalizeFinancialToken(element.id);
  if (FINANCIAL_FIELD_ID_HINTS.has(normalizedId)) {
    return true;
  }

  const normalizedLabel = normalizeFinancialToken(element.label);
  return FINANCIAL_LABEL_HINTS.some((hint) =>
    normalizedLabel.includes(normalizeFinancialToken(hint)),
  );
};

export const WorkflowStartModal: React.FC<WorkflowStartModalProps> = ({
  isOpen,
  onClose,
  projectId,
  workflowId,
  onSuccess,
  isEditMode = false,
  isTemplateChange = false,
  initialData,
  workflowInstanceId,
}) => {
  const { t } = useTranslation([
    "workflows",
    "common",
    "forms",
    "projects",
    "dashboard",
  ]);
  const { user, isAdmin, isManager, isProjectManager } = useAuth(); // Current user context
  const canViewFinancials = isAdmin() || isManager();
  const canManageProjectFileFolders =
    isAdmin() || isManager() || isProjectManager();
  const projectFilesRootLabel = t(
    "workflows:createProjectModal.fileUpload.projectFilesRoot",
  );
  const [formElements, setFormElements] = useState<ProjectFormElement[]>([]);
  const formElementsForUser = useMemo(() => {
    if (canViewFinancials) {
      return formElements;
    }
    return formElements.filter(
      (element) =>
        !FINANCIAL_ELEMENT_TYPES.has(element.type) &&
        !isCurrencySensitiveElement(element),
    );
  }, [canViewFinancials, formElements]);
  const hasProjectCurrencyInputs = useMemo(
    () =>
      formElements.some((element) => MONEY_ELEMENT_TYPES.has(element.type)),
    [formElements],
  );
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [locationSuggestions, setLocationSuggestions] = useState<{
    provinces: string[];
    districts: string[];
    neighborhoods: string[];
  }>({ provinces: [], districts: [], neighborhoods: [] });
  const [isManualProvince, setIsManualProvince] = useState(false);
  const [selectedProvince, setSelectedProvince] = useState<string>("");
  const [selectedDistrict, setSelectedDistrict] = useState<string>("");
  const [isManualDistrict, setIsManualDistrict] = useState<boolean>(false);
  const [isManualNeighborhood, setIsManualNeighborhood] =
    useState<boolean>(false);
  const [valueSuggestions, setValueSuggestions] = useState<
    Record<string, WorkflowFormValueSuggestion[]>
  >({});
  const [selectedSuggestionByElement, setSelectedSuggestionByElement] =
    useState<Record<string, string>>({});
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionsContainerId, setSuggestionsContainerId] = useState<
    number | null
  >(null);
  const [openSuggestionElementId, setOpenSuggestionElementId] = useState<
    string | null
  >(null);
  const openSuggestionRef = useRef<HTMLDivElement | null>(null);
  const [openSelectElementId, setOpenSelectElementId] = useState<string | null>(
    null,
  );
  const openSelectRef = useRef<HTMLDivElement | null>(null);
  const [selectSearch, setSelectSearch] = useState("");
  const [suggestionSearchByElement, setSuggestionSearchByElement] = useState<
    Record<string, string>
  >({});
  const [multiSelectSearchByElement, setMultiSelectSearchByElement] = useState<
    Record<string, string>
  >({});
  const [currentStep, setCurrentStep] = useState(1);
  const [paftaGroups, setPaftaGroups] = useState<PaftaGroup[]>([]);
  const [selectedPaftaStartNodes, setSelectedPaftaStartNodes] = useState<
    string[]
  >([]);
  const [allowedAdHocActivityIds, setAllowedAdHocActivityIds] = useState<
    number[]
  >([]);
  const [availableActivities, setAvailableActivities] = useState<
    ActivityDefinition[]
  >([]);
  const [activitySearchTerm, setActivitySearchTerm] = useState("");
  const [invoiceFilesByElement, setInvoiceFilesByElement] = useState<
    Record<string, QueuedElementUploadFile[]>
  >({});
  const [invoiceUploadsInProgress, setInvoiceUploadsInProgress] = useState<
    Record<string, boolean>
  >({});
  const [invoiceDragOverByElement, setInvoiceDragOverByElement] = useState<
    Record<string, boolean>
  >({});
  const [offerFilesByElement, setOfferFilesByElement] = useState<
    Record<string, QueuedElementUploadFile[]>
  >({});
  const [offerUploadsInProgress, setOfferUploadsInProgress] = useState<
    Record<string, boolean>
  >({});
  const [offerDragOverByElement, setOfferDragOverByElement] = useState<
    Record<string, boolean>
  >({});
  const [projectFiles, setProjectFiles] = useState<ProjectUploadFile[]>([]);
  const [selectedProjectFolderId, setSelectedProjectFolderId] = useState<
    string | null
  >(null);
  const [projectDefaultFolderPath, setProjectDefaultFolderPath] = useState("");
  const [projectDefaultFolderPathEntries, setProjectDefaultFolderPathEntries] =
    useState<FolderPathEntry[]>([{ id: null, name: projectFilesRootLabel }]);
  const [projectFolderPreviewName, setProjectFolderPreviewName] = useState(
    projectFilesRootLabel,
  );
  const [isProjectFolderPreviewLoading, setIsProjectFolderPreviewLoading] =
    useState(false);
  const [projectFolderSelectionFileId, setProjectFolderSelectionFileId] =
    useState<string | null>(null);
  const [showProjectFolderSelectModal, setShowProjectFolderSelectModal] =
    useState(false);
  const [isProjectFileDragOver, setIsProjectFileDragOver] = useState(false);
  const [pendingProjectSubfolderName, setPendingProjectSubfolderName] =
    useState("");
  const [showProjectSubfolderInput, setShowProjectSubfolderInput] =
    useState(false);
  const [virtualProjectFolderPaths, setVirtualProjectFolderPaths] = useState<
    string[][]
  >([]);
  const [isLoadingSelectionData, setIsLoadingSelectionData] = useState(false);
  const [selectionTouched, setSelectionTouched] = useState(false);
  const [initialSelectionConfigured, setInitialSelectionConfigured] =
    useState(false);
  const [initialSelectedPaftaStartNodes, setInitialSelectedPaftaStartNodes] =
    useState<string[]>([]);
  const [initialAllowedAdHocActivityIds, setInitialAllowedAdHocActivityIds] =
    useState<number[]>([]);
  const [initialSelectionLoaded, setInitialSelectionLoaded] = useState(false);
  const selectionInitializedRef = useRef(false);
  const projectFolderInputRef = useRef<HTMLInputElement | null>(null);
  const isTemplateChangeMode =
    Boolean(isTemplateChange) && Boolean(workflowInstanceId);
  const fallbackProjectCurrency = useMemo(() => {
    const storedProjectCurrency =
      formData.projectCurrency ?? formData.currencyCode ?? formData.currency;
    if (storedProjectCurrency) {
      return normalizeCurrencyCode(storedProjectCurrency);
    }

    const legacyCurrencyElement = formElements.find(
      (element) =>
        element.type === "currency_input" || element.type === OFFER_CATEGORY,
    );
    return normalizeCurrencyCode((legacyCurrencyElement as any)?.currency);
  }, [formData, formElements]);
  const elementCurrencies = useMemo<Record<string, string>>(() => {
    const raw = formData.elementCurrencies;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return {};
    }
    return Object.entries(raw as Record<string, unknown>).reduce<
      Record<string, string>
    >((acc, [elementId, currencyCode]) => {
      if (!elementId) {
        return acc;
      }
      acc[elementId] = normalizeCurrencyCode(currencyCode);
      return acc;
    }, {});
  }, [formData.elementCurrencies]);

  const getElementCurrencyCode = useCallback(
    (element: ProjectFormElement): string => {
      const storedCurrency = elementCurrencies[element.id];
      if (storedCurrency) {
        return normalizeCurrencyCode(storedCurrency);
      }
      const configuredCurrency = (element as any)?.currency;
      if (configuredCurrency) {
        return normalizeCurrencyCode(configuredCurrency);
      }
      return fallbackProjectCurrency;
    },
    [elementCurrencies, fallbackProjectCurrency],
  );

  const getElementCurrencySymbol = useCallback(
    (element: ProjectFormElement): string => {
      const currencyCode = getElementCurrencyCode(element);
      return PROJECT_CURRENCY_SYMBOLS[currencyCode] || currencyCode;
    },
    [getElementCurrencyCode],
  );

  // Country options
  const countryOptions = React.useMemo(() => {
    if (countries.length === 0) {
      return [t("forms:countries.turkey"), t("forms:countries.germany")];
    }
    const normalize = (value: string) => value.toLocaleLowerCase("tr-TR");
    const mapped = countries.map((country) => {
      if (country.code === "TR") {
        return t("forms:countries.turkey");
      }
      if (country.code === "DE") {
        return t("forms:countries.germany");
      }
      return country.name;
    });
    const uniqueOptions = Array.from(
      new Map(mapped.map((option) => [normalize(option), option])).values(),
    );
    const priority = [t("forms:countries.turkey"), t("forms:countries.germany")];
    const prioritySet = new Set(priority.map(normalize));
    const prioritized = priority.filter((label) =>
      uniqueOptions.some((option) => normalize(option) === normalize(label)),
    );
    const rest = uniqueOptions
      .filter((option) => !prioritySet.has(normalize(option)))
      .sort((a, b) => a.localeCompare(b, "tr", { sensitivity: "base" }));

    return [...prioritized, ...rest];
  }, [countries, t]);

  const countryCodeByName = React.useMemo(() => {
    const map = new Map<string, string>();
    if (countries.length === 0) {
      map.set(t("forms:countries.turkey"), "TR");
      map.set(t("forms:countries.germany"), "DE");
      return map;
    }
    countries.forEach((country) => {
      const label =
        country.code === "TR"
          ? t("forms:countries.turkey")
          : country.code === "DE"
            ? t("forms:countries.germany")
            : country.name;
      map.set(label, country.code);
    });
    return map;
  }, [countries, t]);

  const LOCATION_TYPES = useMemo(
    () =>
      new Set([
        "country_select",
        "province_select",
        "district_select",
        "neighborhood_select",
      ]),
    [],
  );

  const selectedCountryElementId = useMemo(() => {
    const element = formElements.find((el) => el.type === "country_select");
    return element?.id;
  }, [formElements]);
  const selectedCountryValue = selectedCountryElementId
    ? formData[selectedCountryElementId]
    : "";
  const selectedCountryCode = selectedCountryValue
    ? countryCodeByName.get(String(selectedCountryValue)) || ""
    : "";
  const isTurkeySelected = !selectedCountryCode || selectedCountryCode === "TR";

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const users = await apiService.getUsers(true);
        setEmployees(users);
      } catch (error) {
      }
    };

    if (isOpen) {
      fetchEmployees();
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data = await apiService.getCountries();
        setCountries(data || []);
      } catch (error) {
        setCountries([]);
      }
    };

    if (isOpen) {
      fetchCountries();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!selectedCountryValue || isTurkeySelected) {
      setLocationSuggestions({
        provinces: [],
        districts: [],
        neighborhoods: [],
      });
      return;
    }
    const fetchProvinces = async () => {
      try {
        const response = await apiService.getLocationSuggestions({
          country: String(selectedCountryValue),
        });
        setLocationSuggestions({
          provinces: response.provinces || [],
          districts: [],
          neighborhoods: [],
        });
      } catch (error) {
        setLocationSuggestions({
          provinces: [],
          districts: [],
          neighborhoods: [],
        });
      }
    };
    fetchProvinces();
  }, [isOpen, selectedCountryValue, selectedCountryCode, isTurkeySelected]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!selectedCountryValue || isTurkeySelected || !selectedProvince) {
      setLocationSuggestions((prev) => ({
        ...prev,
        districts: [],
        neighborhoods: [],
      }));
      return;
    }
    const fetchDistricts = async () => {
      try {
        const response = await apiService.getLocationSuggestions({
          country: String(selectedCountryValue),
          province: String(selectedProvince),
        });
        setLocationSuggestions((prev) => ({
          ...prev,
          districts: response.districts || [],
          neighborhoods: [],
        }));
      } catch (error) {
        setLocationSuggestions((prev) => ({
          ...prev,
          districts: [],
          neighborhoods: [],
        }));
      }
    };
    fetchDistricts();
  }, [
    isOpen,
    selectedCountryValue,
    selectedCountryCode,
    isTurkeySelected,
    selectedProvince,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (
      !selectedCountryValue ||
      isTurkeySelected ||
      !selectedProvince ||
      !selectedDistrict
    ) {
      setLocationSuggestions((prev) => ({ ...prev, neighborhoods: [] }));
      return;
    }
    const fetchNeighborhoods = async () => {
      try {
        const response = await apiService.getLocationSuggestions({
          country: String(selectedCountryValue),
          province: String(selectedProvince),
          district: String(selectedDistrict),
        });
        setLocationSuggestions((prev) => ({
          ...prev,
          neighborhoods: response.neighborhoods || [],
        }));
      } catch (error) {
        setLocationSuggestions((prev) => ({ ...prev, neighborhoods: [] }));
      }
    };
    fetchNeighborhoods();
  }, [
    isOpen,
    selectedCountryValue,
    selectedCountryCode,
    isTurkeySelected,
    selectedProvince,
    selectedDistrict,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setCurrentStep(1);
    setActivitySearchTerm("");
    setInvoiceFilesByElement({});
    setInvoiceUploadsInProgress({});
    setInvoiceDragOverByElement({});
    setOfferFilesByElement({});
    setOfferUploadsInProgress({});
    setOfferDragOverByElement({});
    setProjectFiles([]);
    setSelectedProjectFolderId(null);
    setProjectDefaultFolderPath("");
    setProjectDefaultFolderPathEntries([{ id: null, name: projectFilesRootLabel }]);
    setProjectFolderPreviewName(projectFilesRootLabel);
    setIsProjectFolderPreviewLoading(false);
    setProjectFolderSelectionFileId(null);
    setShowProjectFolderSelectModal(false);
    setIsProjectFileDragOver(false);
    setPendingProjectSubfolderName("");
    setShowProjectSubfolderInput(false);
    setVirtualProjectFolderPaths([]);
    setSelectionTouched(false);
    selectionInitializedRef.current = false;
    setPaftaGroups([]);
    setSelectedPaftaStartNodes([]);
    setAllowedAdHocActivityIds([]);
    setAvailableActivities([]);
    setInitialSelectionConfigured(false);
    setInitialSelectedPaftaStartNodes([]);
    setInitialAllowedAdHocActivityIds([]);
    setInitialSelectionLoaded(
      !(isEditMode && workflowInstanceId && workflowInstanceId > 0),
    );
    setLocationSuggestions({ provinces: [], districts: [], neighborhoods: [] });
    setIsManualProvince(false);
    setIsManualDistrict(false);
    setIsManualNeighborhood(false);
    setSuggestionSearchByElement({});
    setMultiSelectSearchByElement({});
  }, [isOpen, isEditMode, workflowInstanceId]);

  useEffect(() => {
    if (!openSuggestionElementId) {
      setSuggestionSearchByElement({});
    }
  }, [openSuggestionElementId]);

  useEffect(() => {
    if (!openSelectElementId) {
      setSelectSearch("");
      return;
    }
  }, [openSelectElementId]);

  useEffect(() => {
    if (!isOpen || !canManageProjectFileFolders || !workflowId || workflowId <= 0) {
      return;
    }

    const pickerInstanceId =
      workflowInstanceId && workflowInstanceId > 0 ? workflowInstanceId : null;
    const canUsePicker = Boolean(pickerInstanceId);
    const shouldShowFileUpload =
      !isTemplateChangeMode && (!isEditMode || Boolean(pickerInstanceId));

    if (!shouldShowFileUpload || canUsePicker) {
      setIsProjectFolderPreviewLoading(false);
      return;
    }

    let isCancelled = false;
    setIsProjectFolderPreviewLoading(true);

    const timer = setTimeout(async () => {
      try {
        const preview = await apiService.previewProjectFolderName(
          workflowId,
          formData || {},
          projectId && projectId > 0 ? projectId : undefined,
        );
        const projectCode = preview?.projectCode?.trim();
        const folderName = preview?.folderName?.trim();
        const typedProjectName = String((formData as any)?.name ?? "").trim();
        const previewProjectName = typedProjectName || t("projects:projectName");
        const previewLabel = projectCode
          ? `${projectCode}-${previewProjectName}`
          : folderName || preview?.projectName?.trim() || projectFilesRootLabel;
        if (!isCancelled) {
          setProjectFolderPreviewName(previewLabel);
          setProjectDefaultFolderPathEntries((prev) => {
            const subPathEntries = prev.length > 1 ? prev.slice(1) : [];
            return [{ id: null, name: previewLabel }, ...subPathEntries];
          });
          setProjectFiles((prev) =>
            prev.map((projectFile) => {
              const currentPathEntries =
                projectFile.folderPathEntries &&
                projectFile.folderPathEntries.length > 0
                  ? projectFile.folderPathEntries
                  : [{ id: null, name: previewLabel }];
              const subPathEntries =
                currentPathEntries.length > 1 ? currentPathEntries.slice(1) : [];
              return {
                ...projectFile,
                folderPathEntries: [{ id: null, name: previewLabel }, ...subPathEntries],
              };
            }),
          );
        }
      } catch (error) {
        if (!isCancelled) {
          const fallbackLabel = projectFilesRootLabel;
          setProjectFolderPreviewName(fallbackLabel);
          setProjectDefaultFolderPathEntries((prev) => {
            const subPathEntries = prev.length > 1 ? prev.slice(1) : [];
            return [{ id: null, name: fallbackLabel }, ...subPathEntries];
          });
        }
      } finally {
        if (!isCancelled) {
          setIsProjectFolderPreviewLoading(false);
        }
      }
    }, 250);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [
    canManageProjectFileFolders,
    formData,
    isEditMode,
    isOpen,
    isTemplateChangeMode,
    projectId,
    workflowId,
    workflowInstanceId,
  ]);

  const normalizeText = useCallback(
    (value: string) => value.toLocaleLowerCase("tr-TR"),
    [],
  );

  // Province options from turkey-locations.json (İZMİR, İSTANBUL, ANKARA pinned on top)
  const provinceOptions = React.useMemo(() => {
    const provinces = getProvinces();
    const priorityOrder = ["İZMİR", "İSTANBUL", "ANKARA"];
    const orderedPriority = priorityOrder
      .map((name) =>
        provinces.find(
          (province) => normalizeText(province) === normalizeText(name),
        ),
      )
      .filter((province): province is string => Boolean(province));
    const prioritySet = new Set(orderedPriority.map(normalizeText));
    const rest = provinces.filter(
      (province) => !prioritySet.has(normalizeText(province)),
    );
    return [...orderedPriority, ...rest];
  }, [normalizeText]);

  const turkeyProvinceSet = useMemo(
    () => new Set(provinceOptions.map(normalizeText)),
    [provinceOptions, normalizeText],
  );

  const turkeyDistrictSet = useMemo(() => {
    const set = new Set<string>();
    Object.values(TURKEY_LOCATIONS).forEach((districts) => {
      Object.keys(districts).forEach((district) => {
        set.add(normalizeText(district));
      });
    });
    return set;
  }, [normalizeText]);

  const turkeyNeighborhoodSet = useMemo(() => {
    const set = new Set<string>();
    Object.values(TURKEY_LOCATIONS).forEach((districts) => {
      Object.values(districts).forEach((neighborhoods) => {
        neighborhoods.forEach((name) => set.add(normalizeText(name)));
      });
    });
    return set;
  }, [normalizeText]);

  const SUGGESTION_SUPPORTED_TYPES = new Set([
    "text",
    "country_select",
    "province_select",
    "district_select",
    "neighborhood_select",
  ]);
  const NEW_ENTRY_OPTION = "__NEW__";

  const cityOptions: Record<string, string[]> = {
    [t("forms:countries.turkey")]: [
      "Adana",
      "Adıyaman",
      "Afyonkarahisar",
      "Ağrı",
      "Aksaray",
      "Amasya",
      "Ankara",
      "Antalya",
      "Ardahan",
      "Artvin",
      "Aydın",
      "Balıkesir",
      "Bartın",
      "Batman",
      "Bayburt",
      "Bilecik",
      "Bingöl",
      "Bitlis",
      "Bolu",
      "Burdur",
      "Bursa",
      "Çanakkale",
      "Çankırı",
      "Çorum",
      "Denizli",
      "Diyarbakır",
      "Düzce",
      "Edirne",
      "Elazığ",
      "Erzincan",
      "Erzurum",
      "Eskişehir",
      "Gaziantep",
      "Giresun",
      "Gümüşhane",
      "Hakkari",
      "Hatay",
      "Iğdır",
      "Isparta",
      "İstanbul",
      "İzmir",
      "Kahramanmaraş",
      "Karabük",
      "Karaman",
      "Kars",
      "Kastamonu",
      "Kayseri",
      "Kilis",
      "Kırıkkale",
      "Kırklareli",
      "Kırşehir",
      "Kocaeli",
      "Konya",
      "Kütahya",
      "Malatya",
      "Manisa",
      "Mardin",
      "Mersin",
      "Muğla",
      "Muş",
      "Nevşehir",
      "Niğde",
      "Ordu",
      "Osmaniye",
      "Rize",
      "Sakarya",
      "Samsun",
      "Şanlıurfa",
      "Siirt",
      "Sinop",
      "Şırnak",
      "Sivas",
      "Tekirdağ",
      "Tokat",
      "Trabzon",
      "Tunceli",
      "Uşak",
      "Van",
      "Yalova",
      "Yozgat",
      "Zonguldak",
    ],
    [t("forms:countries.germany")]: [
      "Berlin",
      "Hamburg",
      "München",
      "Köln",
      "Frankfurt am Main",
      "Stuttgart",
      "Düsseldorf",
      "Dortmund",
      "Essen",
      "Leipzig",
      "Bremen",
      "Dresden",
      "Hannover",
      "Nürnberg",
      "Duisburg",
      "Bochum",
      "Wuppertal",
      "Bielefeld",
      "Bonn",
      "Münster",
      "Karlsruhe",
      "Mannheim",
      "Augsburg",
      "Wiesbaden",
      "Gelsenkirchen",
      "Mönchengladbach",
      "Braunschweig",
      "Chemnitz",
      "Kiel",
      "Aachen",
      "Halle",
      "Magdeburg",
      "Freiburg",
      "Krefeld",
      "Lübeck",
      "Oberhausen",
      "Erfurt",
      "Mainz",
      "Rostock",
      "Kassel",
      "Hagen",
      "Hamm",
      "Saarbrücken",
      "Mülheim",
      "Potsdam",
      "Ludwigshafen",
      "Oldenburg",
      "Leverkusen",
      "Osnabrück",
      "Solingen",
      "Heidelberg",
      "Herne",
      "Neuss",
      "Darmstadt",
      "Paderborn",
      "Regensburg",
      "Ingolstadt",
      "Würzburg",
      "Fürth",
      "Wolfsburg",
      "Offenbach",
      "Ulm",
      "Heilbronn",
      "Pforzheim",
      "Göttingen",
      "Bottrop",
      "Trier",
      "Recklinghausen",
      "Reutlingen",
      "Bremerhaven",
      "Koblenz",
      "Bergisch Gladbach",
      "Jena",
      "Remscheid",
      "Erlangen",
      "Moers",
      "Siegen",
      "Hildesheim",
      "Salzgitter",
    ],
  };

  // Translate placeholder keys e.g. "forms:elementPlaceholders.customTextField" or "elementPlaceholders.customTextField"
  const getPlaceholder = useCallback(
    (placeholder?: string) => {
      if (!placeholder) return "";

      // If already namespaced, try direct
      if (placeholder.includes(":")) {
        const direct = t(placeholder as any);
        return direct;
      }

      // Try common namespaces
      const candidates = [
        `forms:${placeholder}`,
        `workflows:${placeholder}`,
        `common:${placeholder}`,
      ];

      for (const key of candidates) {
        const translated = t(key as any);
        // i18next typically returns the key itself when missing
        if (translated !== key) {
          return translated;
        }
      }

      // Fallback to raw placeholder text
      return placeholder;
    },
    [t],
  );

  const getTranslatedGeneralElementLabel = useCallback(
    (element: ProjectFormElement): string => {
      const normalizedId = normalizeFinancialToken(element.id);
      const normalizedType = normalizeFinancialToken(element.type);

      const labelKeyById: Record<string, string> = {
        name: "projects:projectName",
        projectname: "projects:projectName",
        description: "projects:projectDescription",
        projectdescription: "projects:projectDescription",
        clientname: "projects:clientName",
        client: "projects:client",
        startdate: "projects:startDate",
        enddate: "projects:endDate",
        budget: "projects:budget",
        projectbudget: "projects:budget",
        estimateddurationdays: "projects:projectDurationDays",
        projectduration: "projects:projectDurationDays",
      };

      const labelKeyByType: Record<string, string> = {
        project_duration: "projects:projectDurationDays",
        budget_input: "projects:budget",
        currency_input: "forms:elementLabels.currencyInput",
        offer_amount: "forms:elementLabels.offerAmountInput",
        country_select: "projects:country",
        province_select: "projects:city",
      };

      const translationKey =
        labelKeyById[normalizedId] || labelKeyByType[normalizedType];
      if (!translationKey) {
        return element.label;
      }

      return t(translationKey as any, { defaultValue: element.label });
    },
    [t],
  );

  const buildPaftaGroups = useCallback(
    (workflow: Workflow | null): PaftaGroup[] => {
      if (!workflow) {
        return [];
      }

      const nodeMap = new Map(workflow.nodes.map((node) => [node.id, node]));
      const outgoing = new Map<string, string[]>();

      workflow.edges.forEach((edge) => {
        if (!edge.source || !edge.target) {
          return;
        }
        if (!outgoing.has(edge.source)) {
          outgoing.set(edge.source, []);
        }
        outgoing.get(edge.source)!.push(edge.target);
      });

      const startNodes = workflow.nodes.filter(
        (node) => node.type === NodeType.START,
      );

      return startNodes.map((startNode, index) => {
        const visited = new Set<string>();
        const queue: string[] = [startNode.id];
        const nodeIds: string[] = [];
        const activityIds = new Set<number>();

        while (queue.length > 0) {
          const currentId = queue.shift();
          if (!currentId || visited.has(currentId)) {
            continue;
          }
          visited.add(currentId);
          nodeIds.push(currentId);

          const currentNode = nodeMap.get(currentId);
          const activityId = (currentNode?.data as any)?.activityDefinitionId;
          if (activityId !== undefined && activityId !== null) {
            const parsedId = Number(activityId);
            if (!Number.isNaN(parsedId)) {
              activityIds.add(parsedId);
            }
          }

          if (currentNode?.type === NodeType.END) {
            continue;
          }

          for (const next of outgoing.get(currentId) || []) {
            if (!visited.has(next)) {
              queue.push(next);
            }
          }
        }

        const label =
          startNode.data?.label ||
          startNode.data?.description ||
          `Aktivite ${index + 1}`;

        return {
          startNodeId: startNode.id,
          label,
          description: startNode.data?.description,
          nodeIds,
          activityIds: Array.from(activityIds),
        };
      });
    },
    [],
  );

  const handlePaftaToggle = (startNodeId: string) => {
    setSelectedPaftaStartNodes((prev) => {
      if (prev.includes(startNodeId)) {
        return prev.filter((id) => id !== startNodeId);
      }
      return [...prev, startNodeId];
    });
    setSelectionTouched(true);
  };

  const handleSelectAllPaftas = () => {
    const allStartNodes = paftaGroups.map((group) => group.startNodeId);
    setSelectedPaftaStartNodes(allStartNodes);
    setSelectionTouched(true);
  };

  const handleClearPaftas = () => {
    setSelectedPaftaStartNodes([]);
    setSelectionTouched(true);
  };

  const handleToggleAllowedActivity = (activityId: number) => {
    setAllowedAdHocActivityIds((prev) => {
      if (prev.includes(activityId)) {
        return prev.filter((id) => id !== activityId);
      }
      return [...prev, activityId];
    });
    setSelectionTouched(true);
  };

  useEffect(() => {
    if (isOpen && user) {
      loadFormData();
      // Edit mode or template change: load members from workflow instance
      if (
        (isEditMode || isTemplateChangeMode) &&
        workflowInstanceId &&
        workflowInstanceId > 0
      ) {
        loadMembersFromWorkflowInstance();
      } else if (projectId && projectId > 0) {
        loadMembers();
      } else {
        // No project ID, load all users for member selection
        loadAllUsers();
      }
    }
  }, [
    isOpen,
    projectId,
    user,
    workflowId,
    isEditMode,
    isTemplateChangeMode,
    workflowInstanceId,
  ]);

  useEffect(() => {
    if (!isOpen) return;

    const loadSelectionData = async () => {
      setIsLoadingSelectionData(true);
      try {
        let workflowData: Workflow | null = null;
        if (workflowId && workflowId > 0) {
          workflowData = await apiService.getWorkflowById(workflowId);
        }
        setPaftaGroups(buildPaftaGroups(workflowData));

        // activeOnly: false to ensure we get archived activities too (for lookup names)
        const activitiesData = await apiService.getAllActivities({
          activeOnly: false,
        });
        setAvailableActivities(activitiesData || []);
      } catch (error) {
        setPaftaGroups([]);
        setAvailableActivities([]);
      } finally {
        setIsLoadingSelectionData(false);
      }
    };

    loadSelectionData();
  }, [isOpen, workflowId, buildPaftaGroups]);

  useEffect(() => {
    if (!isOpen) return;
    if (selectionInitializedRef.current) return;
    if (isLoadingSelectionData || !initialSelectionLoaded) return;

    const validStartNodes = new Set(
      paftaGroups.map((group) => group.startNodeId),
    );
    const defaultStartNodes = paftaGroups.map((group) => group.startNodeId);

    let nextStartNodes = defaultStartNodes;
    let nextAllowedActivities: number[] = [];

    if (isEditMode && initialSelectionConfigured) {
      nextStartNodes = (initialSelectedPaftaStartNodes || []).filter((id) =>
        validStartNodes.has(id),
      );
      nextAllowedActivities = initialAllowedAdHocActivityIds || [];
    }

    setSelectedPaftaStartNodes(nextStartNodes);
    setAllowedAdHocActivityIds(nextAllowedActivities);
    setSelectionTouched(false);
    selectionInitializedRef.current = true;
  }, [
    isOpen,
    isEditMode,
    isLoadingSelectionData,
    initialSelectionLoaded,
    initialSelectionConfigured,
    initialSelectedPaftaStartNodes,
    initialAllowedAdHocActivityIds,
    paftaGroups,
  ]);

  const loadValueSuggestions = async (
    workflowContainerId: number | undefined,
    currentFormData: Record<string, any>,
  ) => {
    if (!workflowContainerId || workflowContainerId <= 0) {
      setValueSuggestions({});
      setSelectedSuggestionByElement({});
      setSuggestionsContainerId(null);
      return;
    }

    setIsLoadingSuggestions(true);
    setSuggestionsContainerId(workflowContainerId);
    try {
      const suggestions =
        await apiService.getWorkflowContainerValueSuggestions(
          workflowContainerId,
        );
      const grouped = suggestions.reduce<
        Record<string, WorkflowFormValueSuggestion[]>
      >((acc, item) => {
        if (!acc[item.elementId]) {
          acc[item.elementId] = [];
        }
        acc[item.elementId].push(item);
        return acc;
      }, {});
      Object.values(grouped).forEach((items) => {
        items.sort((a, b) =>
          (a.value || "").localeCompare(b.value || "", "tr", {
            sensitivity: "base",
            numeric: true,
          }),
        );
      });

      setValueSuggestions(grouped);
      setSelectedSuggestionByElement((prev) => {
        const next = { ...prev };
        Object.entries(grouped).forEach(([elementId, items]) => {
          const currentValue = currentFormData[elementId];
          const currentText =
            currentValue === undefined || currentValue === null
              ? ""
              : String(currentValue);
          if (currentText && items.some((item) => item.value === currentText)) {
            next[elementId] = currentText;
          } else if (!next[elementId]) {
            next[elementId] = NEW_ENTRY_OPTION;
          }
        });
        return next;
      });
    } catch (error) {
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    if (!openSuggestionElementId) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openSuggestionRef.current &&
        !openSuggestionRef.current.contains(event.target as Node)
      ) {
        setOpenSuggestionElementId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openSuggestionElementId]);

  useEffect(() => {
    if (!openSelectElementId) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openSelectRef.current &&
        !openSelectRef.current.contains(event.target as Node)
      ) {
        setOpenSelectElementId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openSelectElementId]);

  const loadFormData = async () => {
    setIsLoading(true);
    try {
      const applyFormState = async (
        nextFormElements: ProjectFormElement[],
        nextFormData: Record<string, any>,
        suggestionsContainerId: number | undefined,
      ) => {
        setFormElements(nextFormElements);
        setFormData(nextFormData);
        const provinceElement = nextFormElements.find(
          (el) => el.type === "province_select",
        );
        const districtElement = nextFormElements.find(
          (el) => el.type === "district_select",
        );
        setSelectedProvince(
          provinceElement ? String(nextFormData[provinceElement.id] || "") : "",
        );
        setSelectedDistrict(
          districtElement ? String(nextFormData[districtElement.id] || "") : "",
        );
        setIsManualProvince(false);
        setIsManualDistrict(false);
        setIsManualNeighborhood(false);
        await loadValueSuggestions(suggestionsContainerId, nextFormData);
      };

      if (
        isTemplateChangeMode &&
        workflowInstanceId &&
        workflowInstanceId > 0
      ) {
        const instanceResponse =
          await apiService.getWorkflowInstanceFormData(workflowInstanceId);
        const nextFormData = instanceResponse.formData || initialData || {};
        let nextFormElements: ProjectFormElement[] = [];
        let suggestionsContainerId = projectId;

        try {
          const template = await apiService.getWorkflowTemplateById(workflowId);
          if (template?.workflowContainerFormElements) {
            try {
              const parsed = JSON.parse(template.workflowContainerFormElements);
              if (Array.isArray(parsed)) {
                nextFormElements = parsed;
              }
            } catch (e) {
            }
          }
          if (template?.workflowContainerId) {
            suggestionsContainerId = template.workflowContainerId;
          }
        } catch (templateError) {
        }

        if (
          !suggestionsContainerId ||
          suggestionsContainerId <= 0 ||
          nextFormElements.length === 0
        ) {
          try {
            const workflow = await apiService.getWorkflowById(workflowId);
            if (workflow?.workflowContainerId) {
              suggestionsContainerId = workflow.workflowContainerId;
              try {
                const container = await apiService.getWorkflowContainerById(
                  workflow.workflowContainerId,
                );
                const containerElements = container?.formElements;
                if (Array.isArray(containerElements)) {
                  nextFormElements = containerElements;
                } else if (typeof containerElements === "string") {
                  try {
                    const parsed = JSON.parse(containerElements);
                    if (Array.isArray(parsed)) {
                      nextFormElements = parsed;
                    }
                  } catch (e) {
                  }
                }
              } catch (containerError) {
              }
            }
          } catch (workflowError) {
          }
        }

        if (nextFormElements.length === 0) {
          nextFormElements = instanceResponse.formElements || [];
        }

        await applyFormState(
          nextFormElements,
          nextFormData,
          suggestionsContainerId,
        );
        return;
      }

      // Edit mode with workflowInstanceId: load directly from workflow instance
      // This works even if the parent WorkflowContainer is soft-deleted
      if (isEditMode && workflowInstanceId && workflowInstanceId > 0) {
        const response =
          await apiService.getWorkflowInstanceFormData(workflowInstanceId);
        const nextFormElements = response.formElements || [];
        const nextFormData = response.formData || initialData || {};
        await applyFormState(
          nextFormElements,
          nextFormData,
          response.workflowContainerId || projectId,
        );
        setInitialSelectedPaftaStartNodes(
          response.selectedPaftaStartNodes || [],
        );
        setInitialAllowedAdHocActivityIds(
          response.allowedAdHocActivityIds || [],
        );
        setInitialSelectionConfigured(
          Boolean(response.activitySelectionConfigured),
        );
        setInitialSelectionLoaded(true);
        return;
      }

      if (isEditMode && projectId && projectId > 0) {
        // Edit mode for existing project: load form data from project
        const response = await apiService.getLastWorkflowFormData(projectId);
        const nextFormElements = response.formElements || [];
        const nextFormData = initialData ? initialData : response.data || {};
        await applyFormState(nextFormElements, nextFormData, projectId);
        return;
      }

      // New workflow instance (even if project exists): load form elements but NOT data
      // First try to get form elements from workflow template
      let templateWorkflowContainerId: number | undefined;
      try {
        const template = await apiService.getWorkflowTemplateById(workflowId);
        templateWorkflowContainerId = template?.workflowContainerId;
        if (template?.workflowContainerFormElements) {
          try {
            const parsed = JSON.parse(template.workflowContainerFormElements);
            if (Array.isArray(parsed) && parsed.length > 0) {
              await applyFormState(parsed, {}, projectId);
              return;
            }
          } catch (e) {
          }
        }
      } catch (templateError) {
      }

      // Fallback: load form elements from workflow container
      try {
        const workflow = await apiService.getWorkflowById(workflowId);
        const workflowContainerId =
          workflow?.workflowContainerId || templateWorkflowContainerId;
        if (workflowContainerId) {
          const container = await apiService.getWorkflowContainerById(
            workflowContainerId,
          );
          const containerElements = container?.formElements;

          if (Array.isArray(containerElements) && containerElements.length > 0) {
            await applyFormState(containerElements, {}, workflowContainerId);
            return;
          }

          if (typeof containerElements === "string") {
            try {
              const parsed = JSON.parse(containerElements);
              if (Array.isArray(parsed) && parsed.length > 0) {
                await applyFormState(parsed, {}, workflowContainerId);
                return;
              }
            } catch (parseError) {
            }
          }
        }
      } catch (workflowContainerError) {
      }

      // Fallback: try to get form elements from project (but NOT data)
      if (projectId && projectId > 0) {
        try {
          const response = await apiService.getLastWorkflowFormData(projectId);
          await applyFormState(response.formElements || [], {}, projectId);
          return;
        } catch (projectError) {
        }
      }

      // Note: projectFormElements has been removed from Workflow entity
      // Form elements should be loaded from WorkflowContainer.formElements instead
      await applyFormState([], {}, projectId);
    } catch (error) {
      toast.error(t("workflows:formDataLoadError"));
      if (isEditMode && workflowInstanceId && workflowInstanceId > 0) {
        setInitialSelectionLoaded(true);
      }
    } finally {
      setIsLoading(false);
      if (!isEditMode || !workflowInstanceId || workflowInstanceId <= 0) {
        setInitialSelectionLoaded(true);
      }
    }
  };

  const loadMembersFromWorkflowInstance = async () => {
    setIsLoadingMembers(true);
    try {
      // Get member IDs from workflow instance data
      const response = await apiService.getWorkflowInstanceFormData(
        workflowInstanceId!,
      );
      let memberIds = response.projectMembers || [];
      let workflowContainerId = response.workflowContainerId;

      if (isTemplateChangeMode && workflowId && workflowId > 0) {
        try {
          const template = await apiService.getWorkflowById(workflowId);

          if (template?.workflowContainerId) {
            workflowContainerId = template.workflowContainerId;
          }
        } catch (error) {
        }
      }

      // Ensure memberIds are numbers
      memberIds = memberIds.map((id: any) => Number(id));

      // 1. Fetch ALL potential members from the project (WorkflowContainer)
      // This ensures we see unchecked members too
      let allProjectMembers: any[] = [];
      try {
        if (workflowContainerId) {
          allProjectMembers =
            await apiService.getWorkflowContainerMembersEnriched(
              workflowContainerId,
            );
        } else if (projectId) {
          allProjectMembers =
            await apiService.getWorkflowContainerMembersEnriched(projectId);
        }
      } catch (e) {
      }

      // 2. If we couldn't fetch project members (e.g. deleted), fallback to just the saved members
      if (allProjectMembers.length === 0) {
        const allUsers = await apiService.getUsers();
        allProjectMembers = allUsers
          .filter((u: any) => memberIds.includes(u.id))
          .map((u: any) => ({
            userId: u.id,
            firstName: u.firstName,
            lastName: u.lastName,
            username: u.username,
            email: u.email,
            canStartWorkflow: true,
          }));
      }

      // 3. Ensure current user is in the list
      const currentUserId = user?.id ? Number(user.id) : null;
      if (
        currentUserId &&
        user &&
        !allProjectMembers.some((m) => m.userId === currentUserId)
      ) {
        allProjectMembers.push({
          userId: currentUserId,
          firstName: user.firstName,
          lastName: user.lastName,
          username: user.username,
          email: user.email,
          canStartWorkflow: true,
        });
      }

      // 4. Sort: Current user first, then others alphabetically
      allProjectMembers.sort((a: any, b: any) => {
        if (a.userId === currentUserId) return -1;
        if (b.userId === currentUserId) return 1;
        return (a.username || "").localeCompare(b.username || "");
      });

      setMembers(allProjectMembers);

      // 5. Set selected members based on what was saved in the instance

      // Ensure selected members exist in the available list

      const availableIds = new Set(
        allProjectMembers.map((member: any) => member.userId),
      );

      let selected = memberIds.filter((id: number) => availableIds.has(id));

      if (currentUserId && !selected.includes(currentUserId)) {
        selected.push(currentUserId);
      }

      setSelectedMembers(selected);
    } catch (error) {
      // Fallback: at least select current user
      if (user?.id) {
        setSelectedMembers([Number(user.id)]);
      }
    } finally {
      setIsLoadingMembers(false);
    }
  };
  const loadMembers = async () => {
    setIsLoadingMembers(true);
    try {
      const membersData =
        await apiService.getWorkflowContainerMembersEnriched(projectId);
      // Show all members including current user (current user will be disabled in UI)
      const currentUserId = user?.id;
      setMembers(membersData || []);
      // Pre-select all members who can start workflows AND the current user (always pre-selected)
      const preSelected = (membersData || [])
        .filter((m: any) => m.canStartWorkflow || m.userId === currentUserId)
        .map((m: any) => m.userId);
      setSelectedMembers(preSelected);
    } catch (error) {
      toast.error(t("workflows:projectMembersLoadError"));
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const loadAllUsers = async () => {
    setIsLoadingMembers(true);
    try {
      const allUsers = await apiService.getUsers();
      // Convert users to member format
      const membersData = allUsers.map((u: any) => ({
        userId: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        username: u.username,
        email: u.email,
        canStartWorkflow: true, // Default to true for new projects
      }));
      setMembers(membersData);
      // Pre-select current user
      const currentUserId = user?.id;
      if (currentUserId) {
        setSelectedMembers([currentUserId]);
      }
    } catch (error) {
      // If error, at least select current user
      if (user?.id) {
        setSelectedMembers([user.id]);
      }
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const updateFormData = useCallback((fieldId: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }, []);

  const updateElementCurrency = useCallback((elementId: string, currencyCode: string) => {
    setFormData((prev) => ({
      ...prev,
      elementCurrencies: {
        ...(prev.elementCurrencies &&
        typeof prev.elementCurrencies === "object" &&
        !Array.isArray(prev.elementCurrencies)
          ? prev.elementCurrencies
          : {}),
        [elementId]: normalizeCurrencyCode(currencyCode),
      },
    }));
  }, []);

  const normalizeToStringArray = useCallback((value: any): string[] => {
    if (Array.isArray(value)) {
      return value
        .filter((item) => item !== undefined && item !== null && item !== "")
        .map((item) => String(item));
    }
    if (value === undefined || value === null || value === "") {
      return [];
    }
    return [String(value)];
  }, []);

  const normalizeToNumberArray = useCallback((value: any): number[] => {
    return normalizeToStringArray(value)
      .map((item) => Number(item))
      .filter((item) => !Number.isNaN(item));
  }, [normalizeToStringArray]);

  const commitStringSelection = useCallback(
    (fieldId: string, selectedValues: string[]) => {
      const uniqueValues = Array.from(new Set(selectedValues));
      if (uniqueValues.length === 0) {
        updateFormData(fieldId, "");
      } else if (uniqueValues.length === 1) {
        updateFormData(fieldId, uniqueValues[0]);
      } else {
        updateFormData(fieldId, uniqueValues);
      }
    },
    [updateFormData],
  );

  const commitNumberSelection = useCallback(
    (fieldId: string, selectedValues: number[]) => {
      const uniqueValues = Array.from(new Set(selectedValues));
      if (uniqueValues.length === 0) {
        updateFormData(fieldId, "");
      } else if (uniqueValues.length === 1) {
        updateFormData(fieldId, uniqueValues[0]);
      } else {
        updateFormData(fieldId, uniqueValues);
      }
    },
    [updateFormData],
  );

  const supportsMultipleSelection = useCallback(
    (element: ProjectFormElement) => {
      const locationTypes = new Set<ProjectFormElement["type"]>([
        "country_select",
        "province_select",
        "district_select",
        "neighborhood_select",
      ]);
      const multiSupportedTypes = new Set<ProjectFormElement["type"]>([
        "select",
        "team_member_select",
        "project_type_select",
        "progress_method_select",
        "client_select",
        "employee_select",
      ]);

      return (
        multiSupportedTypes.has(element.type) &&
        !locationTypes.has(element.type)
      );
    },
    [],
  );

  const isMultipleSelectionEnabled = useCallback(
    (element: ProjectFormElement) => {
      if (!supportsMultipleSelection(element)) {
        return false;
      }
      if (typeof element.allowMultiple === "boolean") {
        return element.allowMultiple;
      }
      // Default behavior: single selection unless explicitly enabled.
      return false;
    },
    [supportsMultipleSelection],
  );

  const shouldShowSuggestions = (element: ProjectFormElement) =>
    Boolean(element.saveToList) &&
    SUGGESTION_SUPPORTED_TYPES.has(element.type) &&
    element.id !== "name" &&
    !LOCATION_TYPES.has(element.type);

  const coerceSuggestionValue = (
    element: ProjectFormElement,
    suggestionValue: string,
  ) => {
    if (suggestionValue === "") {
      return "";
    }
    switch (element.type) {
      case "budget_input":
      case "currency_input":
      case "offer_amount":
        return parseFloat(suggestionValue) || 0;
      case "number":
      case "project_duration":
      case "warning_threshold":
        return parseInt(suggestionValue, 10) || 0;
      default:
        return suggestionValue;
    }
  };

  const handleSuggestionChange = (
    element: ProjectFormElement,
    selectedValue: string,
  ) => {
    setSelectedSuggestionByElement((prev) => ({
      ...prev,
      [element.id]: selectedValue,
    }));
    if (selectedValue === NEW_ENTRY_OPTION) {
      return;
    }
    updateFormData(element.id, coerceSuggestionValue(element, selectedValue));
  };

  const updateValueWithSuggestionReset = (
    element: ProjectFormElement,
    nextValue: any,
  ) => {
    updateFormData(element.id, nextValue);
    setSelectedSuggestionByElement((prev) => {
      const current = prev[element.id];
      if (current && current !== NEW_ENTRY_OPTION) {
        return { ...prev, [element.id]: NEW_ENTRY_OPTION };
      }
      return prev;
    });
  };

  const parseIntegerInputValue = (rawValue: string): number | "" => {
    if (rawValue.trim() === "") {
      return "";
    }

    const parsed = Number.parseInt(rawValue, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseDecimalInputValue = (rawValue: string): number | "" => {
    if (rawValue.trim() === "") {
      return "";
    }

    const parsed = Number.parseFloat(rawValue);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const handleDeleteSuggestion = async (
    element: ProjectFormElement,
    suggestionId: number,
    suggestionValue: string,
  ) => {
    if (!suggestionsContainerId || suggestionsContainerId <= 0) {
      return;
    }

    try {
      await apiService.deleteWorkflowContainerValueSuggestion(
        suggestionsContainerId,
        suggestionId,
      );
      setValueSuggestions((prev) => ({
        ...prev,
        [element.id]: (prev[element.id] || []).filter(
          (item) => item.id !== suggestionId,
        ),
      }));
      setSelectedSuggestionByElement((prev) => ({
        ...prev,
        [element.id]:
          prev[element.id] === suggestionValue
            ? NEW_ENTRY_OPTION
            : prev[element.id],
      }));
      toast.success(t("common:remove", "Kaldırıldı"));
    } catch (error) {
      toast.error(t("common:error", "Hata"));
    }
  };

  const renderCustomSelect = (
    elementId: string,
    value: string | number,
    placeholder: string,
    options: Array<{ value: string; label: string }>,
    onChange: (nextValue: string) => void,
    disabled?: boolean,
  ) => {
    const normalizedValue =
      value === undefined || value === null ? "" : String(value);
    const selectedOption = options.find(
      (option) => option.value === normalizedValue,
    );
    const displayLabel = selectedOption
      ? selectedOption.label
      : normalizedValue || placeholder;
    const isPlaceholder = !normalizedValue;
    const isOpen = openSelectElementId === elementId;
    const hasOptions = options.length > 0;
    const isDisabled = Boolean(disabled) || !hasOptions;
    const normalizedSearch = selectSearch.toLocaleLowerCase("tr-TR");
    const filteredOptions = normalizedSearch
      ? options.filter((option) =>
          option.label.toLocaleLowerCase("tr-TR").includes(normalizedSearch),
        )
      : options;
    const handleTypeahead = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isDisabled || !hasOptions) {
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectSearch("");
        return;
      }
      if (event.key === "Backspace") {
        event.preventDefault();
        setSelectSearch((prev) => prev.slice(0, -1));
        return;
      }
      if (event.key.length !== 1) {
        return;
      }
      event.preventDefault();
      const normalizedKey = event.key.toLocaleLowerCase("tr-TR");
      const nextSearch = (selectSearch + normalizedKey).toLocaleLowerCase(
        "tr-TR",
      );
      setSelectSearch(nextSearch);
      if (!isOpen) {
        setOpenSelectElementId(elementId);
      }
    };

    return (
      <div className="relative" ref={isOpen ? openSelectRef : undefined}>
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => {
            setOpenSuggestionElementId(null);
            if (isOpen) {
              setOpenSelectElementId(null);
              setSelectSearch("");
            } else {
              setOpenSelectElementId(elementId);
              setSelectSearch("");
            }
          }}
          onKeyDown={handleTypeahead}
          className="relative flex w-full items-center rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <span
            className={`truncate ${
              isPlaceholder ? "text-gray-400" : "text-gray-700"
            }`}
          >
            {displayLabel}
          </span>
          {hasOptions && (
            <ChevronDownIcon
              className={`absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-950 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
        {isOpen && hasOptions && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-100 p-2">
              <input
                type="text"
                value={selectSearch}
                onChange={(event) => setSelectSearch(event.target.value)}
                placeholder={t("common:search", "Ara...")}
                className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    setOpenSelectElementId(null);
                    setSelectSearch("");
                  }
                }}
              />
            </div>
            <div className="max-h-56 overflow-y-auto py-1">
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpenSelectElementId(null);
                  setSelectSearch("");
                }}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                {placeholder}
              </button>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-400">Sonuç yok</div>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setOpenSelectElementId(null);
                      setSelectSearch("");
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {option.label}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMultiSelectWithSearch = (
    elementId: string,
    options: Array<{ value: string; label: string }>,
    selectedValues: string[],
    onSelectionChange: (nextValues: string[]) => void,
  ) => {
    const searchValue = multiSelectSearchByElement[elementId] || "";
    const normalizedSearch = searchValue.trim().toLocaleLowerCase("tr-TR");
    const filteredOptions = normalizedSearch
      ? options.filter((option) =>
          option.label.toLocaleLowerCase("tr-TR").includes(normalizedSearch),
        )
      : options;

    return (
      <div className="space-y-2 rounded-md border border-gray-300 bg-white p-3 max-h-52 overflow-y-auto">
        <input
          type="text"
          value={searchValue}
          onChange={(event) =>
            setMultiSelectSearchByElement((prev) => ({
              ...prev,
              [elementId]: event.target.value,
            }))
          }
          placeholder={t("common:search", "Ara...")}
          className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {options.length === 0 ? (
          <div className="text-sm text-gray-500">
            {t("forms:noOptions", "Seçenek yok")}
          </div>
        ) : filteredOptions.length === 0 ? (
          <div className="text-sm text-gray-500">
            {t("common:noResults", "Sonuç bulunamadı")}
          </div>
        ) : (
          filteredOptions.map((option) => (
            <label key={option.value} className="flex items-center">
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={(event) => {
                  const nextValues = event.target.checked
                    ? [...selectedValues, option.value]
                    : selectedValues.filter((item) => item !== option.value);
                  onSelectionChange(nextValues);
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">{option.label}</span>
            </label>
          ))
        )}
      </div>
    );
  };

  const renderSuggestionSelect = (element: ProjectFormElement) => {
    if (!shouldShowSuggestions(element)) {
      return null;
    }
    const suggestions = valueSuggestions[element.id] || [];
    const countryElement = formElements.find(
      (el) => el.type === "country_select",
    );
    const selectedCountry = countryElement ? formData[countryElement.id] : "";
    const selectedCountryCode = selectedCountry
      ? countryCodeByName.get(String(selectedCountry)) || ""
      : "";
    const shouldFilterTurkeySuggestions =
      Boolean(selectedCountryCode) && selectedCountryCode !== "TR";
    let filteredSuggestions = suggestions;
    if (shouldFilterTurkeySuggestions) {
      if (element.type === "province_select") {
        filteredSuggestions = suggestions.filter(
          (item) => !turkeyProvinceSet.has(normalizeText(item.value || "")),
        );
      } else if (element.type === "district_select") {
        filteredSuggestions = suggestions.filter(
          (item) => !turkeyDistrictSet.has(normalizeText(item.value || "")),
        );
      } else if (element.type === "neighborhood_select") {
        filteredSuggestions = suggestions.filter(
          (item) => !turkeyNeighborhoodSet.has(normalizeText(item.value || "")),
        );
      }
    }
    const selectedValue =
      selectedSuggestionByElement[element.id] || NEW_ENTRY_OPTION;
    const suggestionValueSet = new Set(
      filteredSuggestions.map((item) => item.value),
    );
    const resolvedValue =
      selectedValue !== NEW_ENTRY_OPTION &&
      !suggestionValueSet.has(selectedValue)
        ? NEW_ENTRY_OPTION
        : selectedValue;
    const isOpen = openSuggestionElementId === element.id;
    const suggestionSearch = suggestionSearchByElement[element.id] || "";
    const normalizedSuggestionSearch = suggestionSearch
      .trim()
      .toLocaleLowerCase("tr-TR");
    const visibleSuggestions = normalizedSuggestionSearch
      ? filteredSuggestions.filter((item) =>
          String(item.value || "")
            .toLocaleLowerCase("tr-TR")
            .includes(normalizedSuggestionSearch),
        )
      : filteredSuggestions;
    const displayValue =
      resolvedValue === NEW_ENTRY_OPTION ? "Yeni giriş" : resolvedValue;

    return (
      <div className="mb-2" ref={isOpen ? openSuggestionRef : undefined}>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Kayıtlı değerler
        </label>
        <div className="relative">
          <button
            type="button"
            disabled={isLoadingSuggestions}
            onClick={() => {
              if (filteredSuggestions.length === 0) {
                return;
              }
              setOpenSelectElementId(null);
              setSuggestionSearchByElement((prev) => ({
                ...prev,
                [element.id]: "",
              }));
              setOpenSuggestionElementId((prev) =>
                prev === element.id ? null : element.id,
              );
            }}
            className="relative flex w-full items-center rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <span
              className={`truncate ${
                resolvedValue === NEW_ENTRY_OPTION
                  ? "text-gray-400"
                  : "text-gray-700"
              }`}
            >
              {displayValue}
            </span>
            {filteredSuggestions.length > 0 && (
              <ChevronDownIcon
                className={`absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-950 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            )}
          </button>
          {isOpen && (
            <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
              <div className="border-b border-gray-100 p-2">
                <input
                  type="text"
                  value={suggestionSearch}
                  onChange={(event) =>
                    setSuggestionSearchByElement((prev) => ({
                      ...prev,
                      [element.id]: event.target.value,
                    }))
                  }
                  placeholder={t("common:search", "Ara...")}
                  className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      setOpenSuggestionElementId(null);
                    }
                  }}
                />
              </div>
              <div className="max-h-56 overflow-y-auto py-1">
                <button
                  type="button"
                  onClick={() => {
                    handleSuggestionChange(element, NEW_ENTRY_OPTION);
                    setOpenSuggestionElementId(null);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  Yeni giriş
                </button>
                {filteredSuggestions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    Boş liste
                  </div>
                ) : visibleSuggestions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-400">
                    {t("common:noResults", "Sonuç bulunamadı")}
                  </div>
                ) : (
                  visibleSuggestions.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <button
                        type="button"
                        className="flex-1 truncate text-left"
                        onClick={() => {
                          handleSuggestionChange(element, item.value);
                          setOpenSuggestionElementId(null);
                        }}
                      >
                        {item.value}
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteSuggestion(element, item.id, item.value);
                        }}
                        className="inline-flex items-center justify-center rounded-md p-1 text-rose-600 hover:text-rose-700"
                        title="Listeden kaldır"
                        aria-label="Listeden kaldır"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleInvoiceFilesChange = (
    elementId: string,
    files: FileList | null,
  ) => {
    if (!files) {
      return;
    }
    const nextFiles = Array.from(files).map((file) => ({
      id: createProjectUploadFileId(file),
      file,
      description: "",
    }));
    setInvoiceFilesByElement((prev) => ({
      ...prev,
      [elementId]: [...(prev[elementId] || []), ...nextFiles],
    }));
  };

  const removeInvoiceFile = (elementId: string, fileId: string) => {
    setInvoiceFilesByElement((prev) => {
      const existing = prev[elementId] || [];
      const nextFiles = existing.filter((queuedFile) => queuedFile.id !== fileId);
      if (nextFiles.length === 0) {
        const next = { ...prev };
        delete next[elementId];
        return next;
      }
      return {
        ...prev,
        [elementId]: nextFiles,
      };
    });
  };

  const updateInvoiceFileDescription = (
    elementId: string,
    fileId: string,
    description: string,
  ) => {
    setInvoiceFilesByElement((prev) => {
      const existing = prev[elementId] || [];
      if (existing.length === 0) {
        return prev;
      }
      return {
        ...prev,
        [elementId]: existing.map((queuedFile) =>
          queuedFile.id === fileId
            ? { ...queuedFile, description }
            : queuedFile,
        ),
      };
    });
  };

  const handleInvoiceDragOver = (
    elementId: string,
    event: React.DragEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    setInvoiceDragOverByElement((prev) => ({ ...prev, [elementId]: true }));
  };

  const handleInvoiceDragLeave = (
    elementId: string,
    event: React.DragEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    setInvoiceDragOverByElement((prev) => ({ ...prev, [elementId]: false }));
  };

  const handleInvoiceDrop = (
    elementId: string,
    event: React.DragEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    setInvoiceDragOverByElement((prev) => ({ ...prev, [elementId]: false }));
    handleInvoiceFilesChange(elementId, event.dataTransfer.files);
  };

  const clearInvoiceFiles = (elementId: string) => {
    setInvoiceFilesByElement((prev) => {
      const next = { ...prev };
      delete next[elementId];
      return next;
    });
  };

  const uploadInvoiceFiles = async (instanceId: number) => {
    const entries = Object.entries(invoiceFilesByElement).filter(
      ([elementId, files]) =>
        files.length > 0 && formData[elementId] === INVOICE_YES,
    );
    if (entries.length === 0) {
      return;
    }

    for (const [elementId, files] of entries) {
      setInvoiceUploadsInProgress((prev) => ({ ...prev, [elementId]: true }));
      try {
        for (const queuedFile of files) {
          try {
            await apiService.uploadFileToProject(
              instanceId,
              queuedFile.file,
              queuedFile.description.trim() || undefined,
              INVOICE_SUBFOLDER,
              INVOICE_CATEGORY,
            );
          } catch (error) {
            toast.error("Fatura dosyası yüklenemedi.");
          }
        }
        clearInvoiceFiles(elementId);
      } finally {
        setInvoiceUploadsInProgress((prev) => ({
          ...prev,
          [elementId]: false,
        }));
      }
    }
  };

  const handleOfferFilesChange = (
    elementId: string,
    files: FileList | null,
  ) => {
    if (!files) {
      return;
    }
    const nextFiles = Array.from(files).map((file) => ({
      id: createProjectUploadFileId(file),
      file,
      description: "",
    }));
    setOfferFilesByElement((prev) => ({
      ...prev,
      [elementId]: [...(prev[elementId] || []), ...nextFiles],
    }));
  };

  const removeOfferFile = (elementId: string, fileId: string) => {
    setOfferFilesByElement((prev) => {
      const existing = prev[elementId] || [];
      const nextFiles = existing.filter((queuedFile) => queuedFile.id !== fileId);
      if (nextFiles.length === 0) {
        const next = { ...prev };
        delete next[elementId];
        return next;
      }
      return {
        ...prev,
        [elementId]: nextFiles,
      };
    });
  };

  const updateOfferFileDescription = (
    elementId: string,
    fileId: string,
    description: string,
  ) => {
    setOfferFilesByElement((prev) => {
      const existing = prev[elementId] || [];
      if (existing.length === 0) {
        return prev;
      }
      return {
        ...prev,
        [elementId]: existing.map((queuedFile) =>
          queuedFile.id === fileId
            ? { ...queuedFile, description }
            : queuedFile,
        ),
      };
    });
  };

  const handleOfferDragOver = (
    elementId: string,
    event: React.DragEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    setOfferDragOverByElement((prev) => ({ ...prev, [elementId]: true }));
  };

  const handleOfferDragLeave = (
    elementId: string,
    event: React.DragEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    setOfferDragOverByElement((prev) => ({ ...prev, [elementId]: false }));
  };

  const handleOfferDrop = (
    elementId: string,
    event: React.DragEvent<HTMLElement>,
  ) => {
    event.preventDefault();
    setOfferDragOverByElement((prev) => ({ ...prev, [elementId]: false }));
    handleOfferFilesChange(elementId, event.dataTransfer.files);
  };

  const clearOfferFiles = (elementId: string) => {
    setOfferFilesByElement((prev) => {
      const next = { ...prev };
      delete next[elementId];
      return next;
    });
  };

  const uploadOfferFiles = async (instanceId: number) => {
    const entries = Object.entries(offerFilesByElement).filter(
      ([, files]) => files.length > 0,
    );
    if (entries.length === 0) {
      return;
    }

    for (const [elementId, files] of entries) {
      setOfferUploadsInProgress((prev) => ({ ...prev, [elementId]: true }));
      try {
        for (const queuedFile of files) {
          try {
            await apiService.uploadFileToProject(
              instanceId,
              queuedFile.file,
              queuedFile.description.trim() || undefined,
              OFFER_SUBFOLDER,
              OFFER_CATEGORY,
            );
          } catch (error) {
            toast.error("Teklif dosyası yüklenemedi.");
          }
        }
        clearOfferFiles(elementId);
      } finally {
        setOfferUploadsInProgress((prev) => ({
          ...prev,
          [elementId]: false,
        }));
      }
    }
  };

  const createProjectUploadFileId = (file: File): string =>
    `${file.name}-${file.size}-${file.lastModified}-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

  const getLocalProjectFileRelativeFolderPath = (file: File): string => {
    const relativePath = (file as LocalProjectUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    const segments = relativePath.split("/").filter(Boolean);
    segments.pop();
    return segments.join("/");
  };

  const getLocalProjectFileRootFolderName = (file: File): string => {
    const relativePath = (file as LocalProjectUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    if (!relativePath) {
      return "";
    }
    return relativePath.split("/").filter(Boolean)[0] || "";
  };

  const getLocalProjectFileDisplayPath = (file: File): string => {
    const relativePath = (file as LocalProjectUploadFile).webkitRelativePath
      ?.replace(/\\/g, "/")
      .trim();
    return relativePath || file.name;
  };

  const getProjectFileTargetFolderDisplayPath = (
    projectFile: ProjectUploadFile,
  ): string => {
    const basePath =
      (projectFile.folderPathEntries || [])
        .map((entry) => entry.name)
        .filter(Boolean)
        .join(" / ") || projectFilesRootLabel;
    const relativeFolderPath = getLocalProjectFileRelativeFolderPath(
      projectFile.file,
    );
    if (!relativeFolderPath) {
      return basePath;
    }
    return `${basePath} / ${relativeFolderPath
      .split("/")
      .filter(Boolean)
      .join(" / ")}`;
  };

  const normalizeProjectFolderName = (value?: string | null): string =>
    String(value || "").trim().toLocaleLowerCase("tr-TR");

  const buildUniqueProjectFolderName = (
    baseName: string,
    existingFolderNames: Set<string>,
  ): string => {
    const trimmedBaseName = baseName.trim() || "Klasör";
    let candidate = trimmedBaseName;
    let suffix = 2;

    while (existingFolderNames.has(normalizeProjectFolderName(candidate))) {
      candidate = `${trimmedBaseName} (${suffix})`;
      suffix += 1;
    }

    existingFolderNames.add(normalizeProjectFolderName(candidate));
    return candidate;
  };

  const buildProjectUploadFiles = (files: File[]): ProjectUploadFile[] =>
    files.map((file) => ({
      id: createProjectUploadFileId(file),
      file: file as LocalProjectUploadFile,
      description: "",
      folderId: selectedProjectFolderId,
      folderPath: canManageProjectFileFolders
        ? projectDefaultFolderPath.trim()
        : "",
      folderPathEntries: projectDefaultFolderPathEntries.map((entry) => ({
        ...entry,
      })),
    }));

  const buildRootFolderPathEntries = (
    rootLabel: string = projectFilesRootLabel,
  ): FolderPathEntry[] => [{ id: null, name: rootLabel }];

  const getCurrentProjectRootLabel = () =>
    projectDefaultFolderPathEntries?.[0]?.name ||
    projectFolderPreviewName ||
    projectFilesRootLabel;

  const setProjectDefaultFolderEntries = (entries: FolderPathEntry[]) => {
    const normalizedEntries =
      entries.length > 0
        ? entries
        : buildRootFolderPathEntries(getCurrentProjectRootLabel());
    setProjectDefaultFolderPathEntries(normalizedEntries);
    const relativePath = normalizedEntries
      .slice(1)
      .map((entry) => entry.name)
      .filter(Boolean)
      .join("/");
    setProjectDefaultFolderPath(relativePath);
  };

  const setProjectDefaultFolderFromRelativeSegments = (segments: string[]) => {
    const rootLabel = getCurrentProjectRootLabel();
    const entries: FolderPathEntry[] = [
      { id: null, name: rootLabel },
      ...segments.map((segment) => ({ id: null, name: segment })),
    ];
    setProjectDefaultFolderEntries(entries);
  };

  const handleVirtualProjectFolderPathSelect = (segments: string[]) => {
    setProjectDefaultFolderFromRelativeSegments(segments);
  };

  const registerVirtualProjectFolderPath = (segments: string[]) => {
    const normalizedSegments = segments.map((segment) => segment.trim()).filter(Boolean);
    if (normalizedSegments.length === 0) {
      return;
    }
    setVirtualProjectFolderPaths((prev) => {
      const exists = prev.some(
        (item) =>
          item.length === normalizedSegments.length &&
          item.every((value, index) => value === normalizedSegments[index]),
      );
      if (exists) {
        return prev;
      }
      return [...prev, normalizedSegments];
    });
  };

  const handleCreateProjectSubfolder = () => {
    const rawName = pendingProjectSubfolderName.trim();
    if (!rawName) {
      return;
    }
    const safeName = rawName.replace(/[\\/]/g, "-").trim();
    if (!safeName) {
      return;
    }

    const currentSegments = selectedProjectRelativePath
      ? selectedProjectRelativePath.split("/").filter(Boolean)
      : [];
    const nextSegments = [...currentSegments, safeName];
    registerVirtualProjectFolderPath(nextSegments);
    // Keep the current parent selected. This allows creating multiple sibling
    // folders under the same level (especially under the root) without forcing
    // users to re-select the parent each time.
    setPendingProjectSubfolderName("");
    setShowProjectSubfolderInput(false);
  };

  const handleProjectFilesChange = (files: FileList | null) => {
    if (!files) {
      return;
    }
    const nextFiles = buildProjectUploadFiles(Array.from(files));
    setProjectFiles((prev) => [...prev, ...nextFiles]);
  };

  const removeProjectFile = (fileId: string) => {
    setProjectFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const updateProjectFileFolderPath = (
    fileId: string,
    folderPath: string,
    folderId: string | null = null,
    folderPathEntries: FolderPathEntry[] = [],
  ) => {
    setProjectFiles((prev) =>
      prev.map((file) =>
        file.id === fileId
          ? {
              ...file,
              folderId,
              folderPath,
              folderPathEntries:
                folderPathEntries.length > 0
                  ? folderPathEntries
                  : file.folderPathEntries,
            }
          : file,
      ),
    );
  };

  const updateProjectFileDescription = (fileId: string, description: string) => {
    setProjectFiles((prev) =>
      prev.map((file) =>
        file.id === fileId
          ? {
              ...file,
              description,
            }
          : file,
      ),
    );
  };

  const clearProjectFiles = () => {
    setProjectFiles([]);
    setProjectFolderSelectionFileId(null);
  };

  const handleProjectFileDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsProjectFileDragOver(true);
  };

  const handleProjectFileDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsProjectFileDragOver(false);
  };

  const handleProjectFileDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsProjectFileDragOver(false);
    handleProjectFilesChange(event.dataTransfer.files);
  };

  const uploadProjectFiles = async (instanceId: number) => {
    const folderPathCache = new Map<string, string | null>();
    const rootFolderNameCache = new Map<string, string>();

    const resolveTargetFolderId = async (
      rawFolderPath: string,
    ): Promise<string | null> => {
      const normalizedPath = rawFolderPath
        .replace(/\\/g, "/")
        .replace(/\/+/g, "/")
        .split("/")
        .map((segment) => segment.trim())
        .filter(Boolean)
        .join("/");

      if (!normalizedPath) {
        return null;
      }

      const cached = folderPathCache.get(normalizedPath);
      if (cached !== undefined) {
        return cached;
      }

      let parentFolderId: string | null = null;
      let currentPath = "";

      for (const segment of normalizedPath.split("/")) {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        const cachedSegment = folderPathCache.get(currentPath);
        if (cachedSegment !== undefined) {
          parentFolderId = cachedSegment;
          continue;
        }

        const createdFolder = await apiService.createProjectFolder(
          instanceId,
          segment,
          parentFolderId,
        );
        parentFolderId = createdFolder?.id || null;
        folderPathCache.set(currentPath, parentFolderId);
      }

      folderPathCache.set(normalizedPath, parentFolderId);
      return parentFolderId;
    };

    const resolveUploadFolderIdForProjectFile = async (
      projectFile: ProjectUploadFile,
    ): Promise<string | null> => {
      let baseFolderId: string | null = null;

      if (canManageProjectFileFolders && projectFile.folderId) {
        baseFolderId = projectFile.folderId;
      } else if (
        canManageProjectFileFolders &&
        projectFile.folderPath.trim()
      ) {
        baseFolderId = await resolveTargetFolderId(projectFile.folderPath);
      }

      if (!canManageProjectFileFolders) {
        return baseFolderId;
      }

      const relativeFolderPath = getLocalProjectFileRelativeFolderPath(
        projectFile.file,
      );
      if (!relativeFolderPath) {
        return baseFolderId;
      }

      const segments = relativeFolderPath.split("/").filter(Boolean);
      const baseFolderKey = baseFolderId ?? "__root__";
      let currentParentId = baseFolderId;
      let currentPath = "";

      for (const segment of segments) {
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        const cacheKey = `${baseFolderKey}::${currentPath}`;
        if (folderPathCache.has(cacheKey)) {
          currentParentId = folderPathCache.get(cacheKey) ?? null;
          continue;
        }

        let folderNameToCreate = segment;
        if (!currentPath.includes("/")) {
          const rootFolderName = getLocalProjectFileRootFolderName(
            projectFile.file,
          );
          if (rootFolderName) {
            const rootFolderCacheKey = `${baseFolderKey}::${normalizeProjectFolderName(
              rootFolderName,
            )}`;
            if (rootFolderNameCache.has(rootFolderCacheKey)) {
              folderNameToCreate =
                rootFolderNameCache.get(rootFolderCacheKey) || segment;
            } else {
              const existingItems = await apiService.getWorkflowInstanceFiles(
                instanceId,
                currentParentId,
              );
              const existingFolderNames = new Set(
                existingItems
                  .filter(
                    (existingFile) =>
                      existingFile.mimeType ===
                      "application/vnd.google-apps.folder",
                  )
                  .map((existingFolder) =>
                    normalizeProjectFolderName(existingFolder.name),
                  ),
              );
              folderNameToCreate = buildUniqueProjectFolderName(
                segment,
                existingFolderNames,
              );
              rootFolderNameCache.set(
                rootFolderCacheKey,
                folderNameToCreate,
              );
            }
          }
        }

        const createdFolder = await apiService.createProjectFolder(
          instanceId,
          folderNameToCreate,
          currentParentId,
        );
        currentParentId = createdFolder?.id || null;
        folderPathCache.set(cacheKey, currentParentId);
      }

      return currentParentId;
    };

    if (canManageProjectFileFolders && virtualProjectFolderPaths.length > 0) {
      const virtualPaths = Array.from(
        new Set(
          virtualProjectFolderPaths
            .map((segments) =>
              segments
                .map((segment) => segment.trim())
                .filter(Boolean)
                .join("/"),
            )
            .filter(Boolean),
        ),
      );

      for (const virtualPath of virtualPaths) {
        try {
          await resolveTargetFolderId(virtualPath);
        } catch (error) {
          toast.error(`${virtualPath} klasörü oluşturulamadı.`);
        }
      }
    }

    if (projectFiles.length === 0) {
      return;
    }

    for (const projectFile of projectFiles) {
      try {
        const targetFolderId = await resolveUploadFolderIdForProjectFile(
          projectFile,
        );

        const uploadDescription =
          projectFile.description.trim() || "Proje oluşturma sırasında yüklendi.";

        await apiService.uploadFileToProject(
          instanceId,
          projectFile.file,
          uploadDescription,
          undefined,
          PROJECT_CREATION_UPLOAD_CATEGORY,
          targetFolderId,
        );
      } catch (error) {
        toast.error(`${projectFile.file.name} dosyası yüklenemedi.`);
      }
    }
    clearProjectFiles();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    const missingFields = formElementsForUser
      .filter((el) => el.required)
      .filter((el) => {
        const value = formData[el.id];
        if (Array.isArray(value)) {
          return value.length === 0;
        }
        return value === undefined || value === null || value === "";
      });

    if (missingFields.length > 0) {
      toast.error(
        t("workflows:fillRequiredFields", {
          fields: missingFields.map((f) => f.label).join(", "),
        }),
      );
      if (currentStep === 2) {
        setCurrentStep(1);
      }
      return;
    }

    // Validate at least 1 project member selected
    if (selectedMembers.length === 0) {
      toast.error(t("workflows:selectAtLeastOneMember"));
      if (currentStep === 2) {
        setCurrentStep(1);
      }
      return;
    }

    if (currentStep === 1) {
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2 && isLoadingSelectionData) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Clean form data: remove keys that are not in the current formElements
      // This ensures that deleted form elements are removed from the saved data
      const cleanedFormData: Record<string, any> = {};

      // 1. Keep standard project fields
      // Only keep mandatory fields that map to database columns (Project Name)
      // Other fields (clientName, budget, etc.) should be removed if deleted from the form design
      const standardFields = ["name", "projectName"];

      standardFields.forEach((field) => {
        if (formData[field] !== undefined) {
          cleanedFormData[field] = formData[field];
        }
      });

      // 2. Keep fields that exist in the current form definition
        formElements.forEach((element) => {
          if (formData[element.id] !== undefined) {
            cleanedFormData[element.id] = formData[element.id];
          }
        });

      if (hasProjectCurrencyInputs) {
        const moneyElements = formElements.filter((element) =>
          MONEY_ELEMENT_TYPES.has(element.type),
        );
        const elementCurrenciesPayload = moneyElements.reduce<
          Record<string, string>
        >((acc, element) => {
          acc[element.id] = getElementCurrencyCode(element);
          return acc;
        }, {});
        if (Object.keys(elementCurrenciesPayload).length > 0) {
          cleanedFormData.elementCurrencies = elementCurrenciesPayload;
        }

        const offerAmountElement = moneyElements.find(
          (element) => element.type === OFFER_CATEGORY,
        );
        if (offerAmountElement) {
          const offerCurrency = elementCurrenciesPayload[offerAmountElement.id];
          if (offerCurrency) {
            elementCurrenciesPayload.offer_amount = offerCurrency;
          }
        }
        const referenceCurrency =
          (offerAmountElement
            ? elementCurrenciesPayload[offerAmountElement.id]
            : undefined) || fallbackProjectCurrency;
        cleanedFormData.projectCurrency = normalizeCurrencyCode(referenceCurrency);
      }

      if (
        isTemplateChangeMode &&
        workflowInstanceId &&
        workflowInstanceId > 0
      ) {
        await apiService.changeWorkflowInstanceTemplate(
          workflowInstanceId,
          workflowId,
          cleanedFormData,
          selectedMembers,
          selectedPaftaStartNodes,
          allowedAdHocActivityIds,
        );
        if (workflowInstanceId) {
          await uploadInvoiceFiles(workflowInstanceId);
          await uploadOfferFiles(workflowInstanceId);
        }
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent(NAV_REFRESH_EVENT, {
              detail: { projectInstances: true },
            }),
          );
        }
        toast.success(t("workflows:projectTypeChanged"));
        onClose();
        onSuccess?.();
        setIsSubmitting(false);
        return;
      }

      // Edit mode: update the workflow instance form data directly
      // This works even if the parent WorkflowContainer is soft-deleted
      if (isEditMode && workflowInstanceId && workflowInstanceId > 0) {
        await apiService.updateWorkflowInstanceFormData(
          workflowInstanceId,
          cleanedFormData,
          selectedMembers,
          selectionTouched ? selectedPaftaStartNodes : undefined,
          selectionTouched ? allowedAdHocActivityIds : undefined,
        );
        await uploadInvoiceFiles(workflowInstanceId);
        await uploadOfferFiles(workflowInstanceId);
        await uploadProjectFiles(workflowInstanceId);
        toast.success(t("workflows:detailsUpdated"));
        onClose();
        onSuccess?.();
        setIsSubmitting(false);
        return;
      }

      // Fallback: Edit mode with projectId (legacy)
      if (isEditMode && projectId > 0) {
        await apiService.updateWorkflowContainerFormData(
          projectId,
          cleanedFormData,
        );
        toast.success(t("workflows:detailsUpdated"));
        onClose();
        onSuccess?.();
        setIsSubmitting(false);
        return;
      }

      let finalProjectId = projectId;

      // If no project ID, create a new project first
      if (!projectId || projectId === 0) {
        try {
          // Extract project name from form data
          const projectName =
            formData.name || formData.projectName || "New Project";

          // Create project with form data
          const projectData: any = {
            name: projectName,
            description: formData.description || "",
            clientName: formData.clientName || "",
            startDate: formData.startDate || null,
            endDate: formData.endDate || null,
            budget: formData.budget || null,
            type: formData.type || formData.projectType || null,
            estimatedDurationDays: formData.estimatedDurationDays || null,
            progressCalculationMethod:
              formData.progressCalculationMethod || "TASK_COUNT",
            warningThreshold: formData.warningThreshold || 20,
            formElements: formElements, // Save form elements to project
            elementOrder: JSON.stringify(formElements.map((el: any) => el.id)),
          };

          const newProject =
            await apiService.createWorkflowContainer(projectData);
          finalProjectId = newProject.id;

          // Add selected members to the new project
          // Note: Managers are auto-added by backend, so we only add non-manager members here
          const currentUserId = user?.id;
          const membersToAdd = selectedMembers.filter(
            (id) => id !== currentUserId,
          );
          if (membersToAdd.length > 0) {
            for (const memberId of membersToAdd) {
              try {
                await apiService.addWorkflowContainerMember(
                  finalProjectId,
                  memberId,
                  "team_member",
                  true,
                );
              } catch (memberError) {
              }
            }
          }
        } catch (projectError) {
          toast.error(t("workflows:projectCreationError"));
          setIsSubmitting(false);
          return;
        }
      }

      // Now start the workflow with the project
      const workflowInstance = await apiService.startWorkflowWithForm(
        workflowId,
        cleanedFormData,
        selectedMembers,
        finalProjectId > 0 ? finalProjectId : undefined,
        selectedPaftaStartNodes,
        allowedAdHocActivityIds,
      );
      const createdInstanceId = (workflowInstance as any)?.id;
      if (createdInstanceId) {
        await uploadInvoiceFiles(createdInstanceId);
        await uploadOfferFiles(createdInstanceId);
        await uploadProjectFiles(createdInstanceId);
      }
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(NAV_REFRESH_EVENT, {
            detail: { projectInstances: true },
          }),
        );
      }
      toast.success(t("workflows:workflowStartedSuccessfully"));
      onClose();
      onSuccess?.();
    } catch (error) {
      toast.error(t("workflows:workflowStartError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const renderFormField = (element: ProjectFormElement) => {
    // For new projects (no projectId and not edit mode), don't use element.value defaults
    const isNewProject = (!projectId || projectId === 0) && !isEditMode;
    const value = isNewProject
      ? (formData[element.id] ?? "")
      : (formData[element.id] ?? element.value ?? "");
    const elementLabel = getTranslatedGeneralElementLabel(element);

    switch (element.type) {
      case "heading":
        return (
          <h3 className="text-lg font-semibold text-gray-800 py-2">
            {elementLabel}
          </h3>
        );

      case "text":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            <input
              type="text"
              value={value}
              onChange={(e) =>
                updateValueWithSuggestionReset(element, e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={getPlaceholder(element.placeholder)}
            />
          </div>
        );

      case "textarea":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            <textarea
              value={value}
              onChange={(e) =>
                updateValueWithSuggestionReset(element, e.target.value)
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder={getPlaceholder(element.placeholder)}
            />
          </div>
        );

      case "budget_input": {
        const currencyCode = getElementCurrencyCode(element);
        const currencySymbol = getElementCurrencySymbol(element);
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={value}
              onChange={(e) =>
                updateValueWithSuggestionReset(
                  element,
                  parseDecimalInputValue(e.target.value),
                )
              }
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={getPlaceholder(element.placeholder) || "0.00"}
              />
              <span className="absolute left-3 top-2 text-blue-600 font-semibold">
                {currencySymbol}
              </span>
              <span className="absolute right-3 top-2 text-xs font-medium text-gray-400">
                {currencyCode}
              </span>
            </div>
            {canViewFinancials && (
              <select
                value={currencyCode}
                onChange={(event) =>
                  updateElementCurrency(element.id, event.target.value)
                }
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {PROJECT_CURRENCY_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.symbol} - {option.code} ({option.name})
                  </option>
                ))}
              </select>
            )}
          </div>
        );
      }

      case "currency_input": {
        const currencyCode = getElementCurrencyCode(element);
        const currencySymbol = getElementCurrencySymbol(element);

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={value}
              onChange={(e) =>
                updateValueWithSuggestionReset(
                  element,
                  parseDecimalInputValue(e.target.value),
                )
              }
                className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder={getPlaceholder(element.placeholder) || "0.00"}
              />
              <span className="absolute left-3 top-2 text-green-600 font-semibold">
                {currencySymbol}
              </span>
              <span className="absolute right-3 top-2 text-xs font-medium text-gray-400">
                {currencyCode}
              </span>
            </div>
            {canViewFinancials && (
              <select
                value={currencyCode}
                onChange={(event) =>
                  updateElementCurrency(element.id, event.target.value)
                }
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {PROJECT_CURRENCY_OPTIONS.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.symbol} - {option.code} ({option.name})
                  </option>
                ))}
              </select>
            )}
          </div>
        );
      }

      case "offer_amount": {
        const currencyCode = getElementCurrencyCode(element);
        const currencySymbol = getElementCurrencySymbol(element);
        const offerFiles = offerFilesByElement[element.id] || [];
        const isUploading = offerUploadsInProgress[element.id] || false;
        const isDragOver = offerDragOverByElement[element.id] || false;

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {elementLabel}
                {element.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderSuggestionSelect(element)}
              <div className="relative">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={value}
                  onChange={(e) =>
                    updateValueWithSuggestionReset(
                      element,
                      parseDecimalInputValue(e.target.value),
                    )
                  }
                  className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder={getPlaceholder(element.placeholder) || "0.00"}
                />
                <span className="absolute left-3 top-2 text-green-600 font-semibold">
                  {currencySymbol}
                </span>
                <span className="absolute right-3 top-2 text-xs font-medium text-gray-400">
                  {currencyCode}
                </span>
              </div>
              {canViewFinancials && (
                <select
                  value={currencyCode}
                  onChange={(event) =>
                    updateElementCurrency(element.id, event.target.value)
                  }
                  className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {PROJECT_CURRENCY_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.symbol} - {option.code} ({option.name})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-800">
                  İş Tutarı/Teklif dosyaları
                </p>
                <p className="text-xs text-gray-500">
                  Kayıttan sonra otomatik olarak "İş Tutarı - Teklifler"
                  klasörü açılacak ve dosyalar bu klasöre yüklenecek.
                </p>
              </div>

              <div className="flex items-center justify-center w-full">
                <label
                  className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                    isDragOver
                      ? "border-blue-500 bg-blue-50"
                      : "border-blue-200 bg-blue-50/40 hover:bg-blue-50"
                  }`}
                  onDragOver={(event) => handleOfferDragOver(element.id, event)}
                  onDragLeave={(event) =>
                    handleOfferDragLeave(element.id, event)
                  }
                  onDrop={(event) => handleOfferDrop(element.id, event)}
                >
                  <div className="flex flex-col items-center justify-center pt-4 pb-5">
                    <svg
                      className="w-8 h-8 mb-3 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <p className="mb-1 text-sm text-gray-600">
                      <span className="font-semibold">
                        Dosya seç veya sürükle bırak
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Teklif dosyaları yükleme kuyruğuna alınır.
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    onChange={(e) =>
                      handleOfferFilesChange(element.id, e.target.files)
                    }
                  />
                </label>
              </div>

              {offerFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    {t("dashboard:selectedFiles")} ({offerFiles.length})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {offerFiles.map((queuedFile) => (
                      <div
                        key={queuedFile.id}
                        className="bg-white p-3 rounded-lg border border-green-100 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <DocumentTextIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-700 truncate">
                                {queuedFile.file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(queuedFile.file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              removeOfferFile(element.id, queuedFile.id)
                            }
                            className="ml-2 text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                            aria-label="Dosyayi kaldir"
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-[11px] font-medium text-gray-600 mb-1">
                            Dosya Açıklaması
                          </label>
                          <input
                            type="text"
                            value={queuedFile.description}
                            onChange={(event) =>
                              updateOfferFileDescription(
                                element.id,
                                queuedFile.id,
                                event.target.value,
                              )
                            }
                            placeholder="Bu dosya için kısa bir açıklama yazın"
                            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isUploading && (
                <div className="text-xs text-blue-600">Dosyalar yükleniyor...</div>
              )}
            </div>
          </div>
        );
      }

      case "date":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            <input
              type="date"
              value={value}
              onChange={(e) =>
                updateValueWithSuggestionReset(element, e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        );

      case "number":
      case "project_duration":
      case "warning_threshold":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            <input
              type="number"
              min={element.type === "warning_threshold" ? 1 : 0}
              max={element.type === "warning_threshold" ? 100 : undefined}
              value={value}
              onChange={(e) =>
                updateValueWithSuggestionReset(
                  element,
                  parseIntegerInputValue(e.target.value),
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={getPlaceholder(element.placeholder)}
            />
            {element.type === "warning_threshold" && (
              <p className="mt-1 text-xs text-gray-500">
                {t("workflows:budgetDeviationWarning")}
              </p>
            )}
          </div>
        );

      case "employee_select": {
        const selectedEmployeeIds = normalizeToNumberArray(value);
        const allowMultiple = isMultipleSelectionEnabled(element);

        if (allowMultiple) {
          const employeeOptions = employees.map((emp) => ({
            value: String(emp.id),
            label: `${emp.firstName} ${emp.lastName}`,
          }));
          const selectedEmployeeValueStrings = selectedEmployeeIds.map((id) =>
            String(id),
          );

          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {elementLabel}
                {element.required && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>
              {renderMultiSelectWithSearch(
                element.id,
                employeeOptions,
                selectedEmployeeValueStrings,
                (nextValues) =>
                  commitNumberSelection(
                    element.id,
                    nextValues
                      .map((item) => Number.parseInt(item, 10))
                      .filter((item) => Number.isFinite(item)),
                  ),
              )}
            </div>
          );
        }

        const singleEmployeeValue =
          selectedEmployeeIds.length > 0
            ? String(selectedEmployeeIds[0])
            : value === undefined || value === null
              ? ""
              : String(value);

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderCustomSelect(
              element.id,
              singleEmployeeValue,
              t("workflows:selectOption"),
              employees.map((emp) => ({
                value: String(emp.id),
                label: `${emp.firstName} ${emp.lastName}`,
              })),
              (nextValue) =>
                updateFormData(element.id, nextValue ? Number(nextValue) : ""),
              false,
            )}
          </div>
        );
      }

      case "select": {
        const allowMultiple = isMultipleSelectionEnabled(element);
        const selectedValues = normalizeToStringArray(value);
        const singleSelectedValue = selectedValues[0] || "";

        if (!allowMultiple) {
          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {elementLabel}
                {element.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderCustomSelect(
                element.id,
                singleSelectedValue,
                t("workflows:selectOption"),
                (element.options || []).map((option) => ({
                  value: option,
                  label: option,
                })),
                (nextValue) => updateFormData(element.id, nextValue),
                false,
              )}
            </div>
          );
        }

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderMultiSelectWithSearch(
              element.id,
              (element.options || []).map((option) => ({
                value: option,
                label: option,
              })),
              selectedValues,
              (nextValues) => commitStringSelection(element.id, nextValues),
            )}
            {/*
            <div className="space-y-2 rounded-md border border-gray-300 bg-white p-3 max-h-52 overflow-y-auto">
              {(element.options || []).length === 0 ? (
                <div className="text-sm text-gray-500">
                  {t("forms:noOptions", "Seçenek yok")}
                </div>
              ) : (
                (element.options || []).map((option) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={(e) => {
                        const nextValues = e.target.checked
                          ? [...selectedValues, option]
                          : selectedValues.filter((item) => item !== option);
                        commitStringSelection(element.id, nextValues);
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {option}
                    </span>
                  </label>
                ))
              )}
            </div>
            */}
          </div>
        );
      }
      case "project_type_select":
      case "progress_method_select":
      case "client_select":
      case "team_member_select": {
        const allowMultiple = isMultipleSelectionEnabled(element);
        const selectedValues = normalizeToStringArray(value);
        const singleSelectedValue = selectedValues[0] || "";

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            {allowMultiple ? (
              <>
              {renderMultiSelectWithSearch(
                element.id,
                (element.options || []).map((option) => ({
                  value: option,
                  label: option,
                })),
                selectedValues,
                (nextValues) => commitStringSelection(element.id, nextValues),
              )}
              {/*
              <div className="space-y-2 rounded-md border border-gray-300 bg-white p-3 max-h-52 overflow-y-auto">
                {(element.options || []).length === 0 ? (
                  <div className="text-sm text-gray-500">
                    {t("forms:noOptions", "Seçenek yok")}
                  </div>
                ) : (
                  (element.options || []).map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(option)}
                        onChange={(e) => {
                          const nextValues = e.target.checked
                            ? [...selectedValues, option]
                            : selectedValues.filter((item) => item !== option);
                          commitStringSelection(element.id, nextValues);
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {option}
                      </span>
                    </label>
                  ))
                )}
              </div>
              */}
              </>
            ) : (
              renderCustomSelect(
                element.id,
                singleSelectedValue,
                t("workflows:selectOption"),
                (element.options || []).map((option) => ({
                  value: option,
                  label: option,
                })),
                (nextValue) => updateValueWithSuggestionReset(element, nextValue),
                false,
              )
            )}
          </div>
        );
      }

      case "country_select":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            {renderCustomSelect(
              element.id,
              value,
              t("workflows:selectOption"),
              countryOptions.map((country) => ({
                value: country,
                label: country,
              })),
              (nextValue) => {
                updateValueWithSuggestionReset(element, nextValue);
                // Reset location fields when country changes
                const provinceElement = formElements.find(
                  (el) => el.type === "province_select",
                );
                const districtElement = formElements.find(
                  (el) => el.type === "district_select",
                );
                const neighborhoodElement = formElements.find(
                  (el) => el.type === "neighborhood_select",
                );
                if (provinceElement) updateFormData(provinceElement.id, "");
                if (districtElement) updateFormData(districtElement.id, "");
                if (neighborhoodElement)
                  updateFormData(neighborhoodElement.id, "");
                setSelectedProvince("");
                setSelectedDistrict("");
                setIsManualProvince(false);
                setIsManualDistrict(false);
                setIsManualNeighborhood(false);
              },
              false,
            )}
          </div>
        );

      case "province_select": {
        // Ülke seçimini kontrol et - sadece Türkiye için çalışır
        const countryElement = formElements.find(
          (el) => el.type === "country_select",
        );
        const countryElementId = countryElement?.id;
        const hasCountryElement = Boolean(countryElementId);
        const selectedCountry = countryElementId
          ? formData[countryElementId]
          : "";
        const selectedCountryCode = selectedCountry
          ? countryCodeByName.get(String(selectedCountry)) || ""
          : "";
        const isTurkey = !hasCountryElement || selectedCountryCode === "TR";
        const needsCountrySelection = hasCountryElement && !selectedCountry;
        const provincePlaceholder = !hasCountryElement
          ? "İl seçiniz"
          : needsCountrySelection
            ? "Önce ülke seçiniz"
            : "İl seçiniz";
        const nonTurkeyProvinceOptions = locationSuggestions.provinces;
        const showNonTurkeyManual =
          hasCountryElement &&
          selectedCountry &&
          !isTurkey &&
          (isManualProvince || nonTurkeyProvinceOptions.length === 0);

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            {showNonTurkeyManual ? (
              <input
                type="text"
                value={value}
                onChange={(e) => {
                  updateValueWithSuggestionReset(element, e.target.value);
                  setSelectedProvince(e.target.value);
                }}
                placeholder="İl adını yazınız"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              renderCustomSelect(
                element.id,
                value,
                provincePlaceholder,
                isTurkey
                  ? provinceOptions.map((province) => ({
                      value: province,
                      label: province,
                    }))
                  : [
                      { value: "__MANUAL__", label: "Manuel Gir" },
                      ...nonTurkeyProvinceOptions.map((province) => ({
                        value: province,
                        label: province,
                      })),
                    ],
                (nextValue) => {
                  if (!isTurkey && nextValue === "__MANUAL__") {
                    setIsManualProvince(true);
                    updateFormData(element.id, "");
                    setSelectedProvince("");
                    return;
                  }
                  setIsManualProvince(false);
                  const newProvince = nextValue;
                  updateValueWithSuggestionReset(element, newProvince);
                  setSelectedProvince(newProvince);

                  // İl değişince ilçe ve mahalleyi sıfırla
                  const districtElement = formElements.find(
                    (el) => el.type === "district_select",
                  );
                  const neighborhoodElement = formElements.find(
                    (el) => el.type === "neighborhood_select",
                  );
                  if (districtElement) updateFormData(districtElement.id, "");
                  if (neighborhoodElement)
                    updateFormData(neighborhoodElement.id, "");
                  setSelectedDistrict("");
                  setIsManualDistrict(false);
                  setIsManualNeighborhood(false);
                },
                hasCountryElement && !selectedCountry,
              )
            )}
          </div>
        );
      }

      case "district_select": {
        // Ülke ve il seçimini kontrol et
        const countryElement = formElements.find(
          (el) => el.type === "country_select",
        );
        const countryElementId = countryElement?.id;
        const hasCountryElement = Boolean(countryElementId);
        const selectedCountry = countryElementId
          ? formData[countryElementId]
          : "";
        const selectedCountryCode = selectedCountry
          ? countryCodeByName.get(String(selectedCountry)) || ""
          : "";
        const isTurkey = !hasCountryElement || selectedCountryCode === "TR";
        const needsCountrySelection = hasCountryElement && !selectedCountry;

        const provinceElement = formElements.find(
          (el) => el.type === "province_select",
        );
        const selectedProv = provinceElement
          ? formData[provinceElement.id]
          : "";
        const availableDistricts =
          isTurkey && selectedProv ? getDistricts(selectedProv) : [];
        const nonTurkeyDistrictOptions = locationSuggestions.districts;
        const locationDisabled = hasCountryElement && !selectedCountry;
        const showNonTurkeyManual =
          hasCountryElement &&
          selectedCountry &&
          !isTurkey &&
          (isManualDistrict || nonTurkeyDistrictOptions.length === 0);

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            {showNonTurkeyManual ? (
              <input
                type="text"
                value={value}
                onChange={(e) => {
                  updateValueWithSuggestionReset(element, e.target.value);
                  setSelectedDistrict(e.target.value);
                }}
                placeholder="İlçe adını yazınız"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : isManualDistrict ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={value}
                  onChange={(e) => {
                    updateValueWithSuggestionReset(element, e.target.value);
                    setSelectedDistrict(e.target.value);
                  }}
                  placeholder="İlçe adını yazınız"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsManualDistrict(false);
                    updateFormData(element.id, "");
                    setSelectedDistrict("");
                    // Mahalleyi sıfırla
                    const neighborhoodElement = formElements.find(
                      (el) => el.type === "neighborhood_select",
                    );
                    if (neighborhoodElement)
                      updateFormData(neighborhoodElement.id, "");
                    setIsManualNeighborhood(false);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Listeden seç
                </button>
              </div>
            ) : (
              renderCustomSelect(
                element.id,
                value,
                needsCountrySelection
                  ? "Önce ülke seçiniz"
                  : !selectedProv
                    ? "Önce il seçiniz"
                    : "İlçe seçiniz",
                [
                  ...(isTurkey && selectedProv
                    ? [{ value: "__MANUAL__", label: "Manuel Gir" }]
                    : []),
                  ...(isTurkey
                    ? availableDistricts.map((district) => ({
                        value: district,
                        label: district,
                      }))
                    : [
                        { value: "__MANUAL__", label: "Manuel Gir" },
                        ...nonTurkeyDistrictOptions.map((district) => ({
                          value: district,
                          label: district,
                        })),
                      ]),
                ],
                (nextValue) => {
                  const newDistrict = nextValue;
                  if (!isTurkey && newDistrict === "__MANUAL__") {
                    setIsManualDistrict(true);
                    updateFormData(element.id, "");
                    setSelectedDistrict("");
                    return;
                  }
                  if (isTurkey && newDistrict === "__MANUAL__") {
                    setIsManualDistrict(true);
                    updateFormData(element.id, "");
                    setSelectedDistrict("");
                  } else {
                    updateValueWithSuggestionReset(element, newDistrict);
                    setSelectedDistrict(newDistrict);
                  }

                  // İlçe değişince mahalleyi sıfırla
                  const neighborhoodElement = formElements.find(
                    (el) => el.type === "neighborhood_select",
                  );
                  if (neighborhoodElement)
                    updateFormData(neighborhoodElement.id, "");
                  setIsManualNeighborhood(false);
                },
                locationDisabled || !selectedProv,
              )
            )}
          </div>
        );
      }

      case "neighborhood_select": {
        // Ülke, il ve ilçe seçimini kontrol et
        const countryElement = formElements.find(
          (el) => el.type === "country_select",
        );
        const countryElementId = countryElement?.id;
        const hasCountryElement = Boolean(countryElementId);
        const selectedCountry = countryElementId
          ? formData[countryElementId]
          : "";
        const selectedCountryCode = selectedCountry
          ? countryCodeByName.get(String(selectedCountry)) || ""
          : "";
        const isTurkey = !hasCountryElement || selectedCountryCode === "TR";
        const needsCountrySelection = hasCountryElement && !selectedCountry;

        const provinceElement = formElements.find(
          (el) => el.type === "province_select",
        );
        const districtElement = formElements.find(
          (el) => el.type === "district_select",
        );
        const selectedProv = provinceElement
          ? formData[provinceElement.id]
          : "";
        const selectedDist = districtElement
          ? formData[districtElement.id]
          : "";
        const availableNeighborhoods =
          isTurkey && selectedProv && selectedDist && !isManualDistrict
            ? getNeighborhoods(selectedProv, selectedDist)
            : [];
        const nonTurkeyNeighborhoodOptions = locationSuggestions.neighborhoods;
        const locationDisabled = hasCountryElement && !selectedCountry;
        const showNonTurkeyManual =
          hasCountryElement &&
          selectedCountry &&
          !isTurkey &&
          (isManualNeighborhood || nonTurkeyNeighborhoodOptions.length === 0);

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {renderSuggestionSelect(element)}
            {showNonTurkeyManual ? (
              <input
                type="text"
                value={value}
                onChange={(e) =>
                  updateValueWithSuggestionReset(element, e.target.value)
                }
                placeholder="Mahalle adını yazınız"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : isManualNeighborhood ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={value}
                  onChange={(e) =>
                    updateValueWithSuggestionReset(element, e.target.value)
                  }
                  placeholder="Mahalle adını yazınız"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    setIsManualNeighborhood(false);
                    updateFormData(element.id, "");
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Listeden seç
                </button>
              </div>
            ) : (
              renderCustomSelect(
                element.id,
                value,
                needsCountrySelection
                  ? "Önce ülke seçiniz"
                  : !selectedProv
                    ? "Önce il seçiniz"
                    : !selectedDist
                      ? "Önce ilçe seçiniz"
                      : "Mahalle seçiniz",
                [
                  ...(isTurkey && selectedProv && selectedDist
                    ? [{ value: "__MANUAL__", label: "Manuel Gir" }]
                    : []),
                  ...(isTurkey
                    ? availableNeighborhoods.map((neighborhood) => ({
                        value: neighborhood,
                        label: neighborhood,
                      }))
                    : [
                        { value: "__MANUAL__", label: "Manuel Gir" },
                        ...nonTurkeyNeighborhoodOptions.map((neighborhood) => ({
                          value: neighborhood,
                          label: neighborhood,
                        })),
                      ]),
                ],
                (nextValue) => {
                  const newNeighborhood = nextValue;
                  if (!isTurkey && newNeighborhood === "__MANUAL__") {
                    setIsManualNeighborhood(true);
                    updateFormData(element.id, "");
                    return;
                  }
                  if (isTurkey && newNeighborhood === "__MANUAL__") {
                    setIsManualNeighborhood(true);
                    updateFormData(element.id, "");
                  } else {
                    updateValueWithSuggestionReset(element, newNeighborhood);
                  }
                },
                locationDisabled || !selectedProv || !selectedDist,
              )
            )}
          </div>
        );
      }

      case INVOICE_CATEGORY: {
        const selectedValue = value || "";
        const invoiceFiles = invoiceFilesByElement[element.id] || [];
        const isUploading = invoiceUploadsInProgress[element.id] || false;
        const needsUpload = selectedValue === INVOICE_YES;
        const canUploadAfterSave =
          !workflowInstanceId || workflowInstanceId === 0;
        const isDragOver = invoiceDragOverByElement[element.id] || false;

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name={element.id}
                  value="yes"
                  checked={selectedValue === INVOICE_YES}
                  onChange={() => updateFormData(element.id, INVOICE_YES)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                {t("common:yes")}
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name={element.id}
                  value="no"
                  checked={selectedValue === INVOICE_NO}
                  onChange={() => {
                    updateFormData(element.id, INVOICE_NO);
                    clearInvoiceFiles(element.id);
                  }}
                  className="text-blue-600 focus:ring-blue-500"
                />
                {t("common:no")}
              </label>
            </div>

            {needsUpload && (
              <div className="mt-4 space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    Fatura dosyası
                  </p>
                  <p className="text-xs text-gray-500">
                    Kayıttan sonra otomatik olarak "Faturalar" klasörü
                    açılacak ve dosyalar bu klasöre yüklenecek.
                  </p>
                </div>

                <div className="flex items-center justify-center w-full">
                  <label
                    className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
                      isDragOver
                        ? "border-blue-500 bg-blue-50"
                        : "border-blue-200 bg-blue-50/40 hover:bg-blue-50"
                    }`}
                    onDragOver={(event) =>
                      handleInvoiceDragOver(element.id, event)
                    }
                    onDragLeave={(event) =>
                      handleInvoiceDragLeave(element.id, event)
                    }
                    onDrop={(event) => handleInvoiceDrop(element.id, event)}
                  >
                    <div className="flex flex-col items-center justify-center pt-4 pb-5">
                      <svg
                        className="w-8 h-8 mb-3 text-blue-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="mb-1 text-sm text-gray-600">
                        <span className="font-semibold">
                          Dosya seç veya sürükle bırak
                        </span>
                      </p>
                      <p className="text-xs text-gray-500">
                        Fatura dosyaları yükleme kuyruğuna alınır.
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      onChange={(e) =>
                        handleInvoiceFilesChange(element.id, e.target.files)
                      }
                    />
                  </label>
                </div>

                {invoiceFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h4 className="text-sm font-medium text-gray-700">
                      {t("dashboard:selectedFiles")} ({invoiceFiles.length})
                    </h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {invoiceFiles.map((queuedFile) => (
                        <div
                          key={queuedFile.id}
                          className="bg-white p-3 rounded-lg border border-green-100 space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <DocumentTextIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-700 truncate">
                                  {queuedFile.file.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {(queuedFile.file.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                removeInvoiceFile(element.id, queuedFile.id)
                              }
                              className="ml-2 text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                              aria-label="Dosyayi kaldir"
                            >
                              <XMarkIcon className="w-5 h-5" />
                            </button>
                          </div>
                          <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                              Dosya Açıklaması
                            </label>
                            <input
                              type="text"
                              value={queuedFile.description}
                              onChange={(event) =>
                                updateInvoiceFileDescription(
                                  element.id,
                                  queuedFile.id,
                                  event.target.value,
                                )
                              }
                              placeholder="Bu dosya için kısa bir açıklama yazın"
                              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isUploading && (
                  <div className="text-xs text-blue-600">
                    Dosyalar yükleniyor...
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case "checkbox":
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={element.id}
              checked={value || false}
              onChange={(e) => updateFormData(element.id, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor={element.id} className="ml-2 text-sm text-gray-700">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          </div>
        );

      case "notification_roles":
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {element.options?.map((option, index) => (
                <label key={index} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={(value || []).includes(option)}
                    onChange={(e) => {
                      const currentValues = value || [];
                      if (e.target.checked) {
                        updateFormData(element.id, [...currentValues, option]);
                      } else {
                        updateFormData(
                          element.id,
                          currentValues.filter((v: string) => v !== option),
                        );
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {elementLabel}
              {element.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => updateFormData(element.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={getPlaceholder(element.placeholder)}
            />
          </div>
        );
    }
  };

  const filteredExtraActivities = availableActivities
    .filter((activity) => {
      const term = activitySearchTerm.trim().toLowerCase();
      if (!term) return true;
      return (
        activity.title?.toLowerCase().includes(term) ||
        activity.description?.toLowerCase().includes(term) ||
        activity.tags?.toLowerCase().includes(term)
      );
    })
    .sort((a, b) => {
      const aTitle = a.title || "";
      const bTitle = b.title || "";
      return aTitle.localeCompare(bTitle);
    });

  const activityLookup = useMemo(() => {
    const map = new Map<number, ActivityDefinition>();
    availableActivities.forEach((activity) => {
      map.set(activity.id, activity);
    });
    return map;
  }, [availableActivities]);

  const handleViewWorkflow = () => {
    if (!workflowId) return;
    if (typeof window === "undefined") return;
    window.open(
      `/workflows/${workflowId}/view`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  if (!isOpen) return null;
  const projectFolderPickerWorkflowInstanceId =
    workflowInstanceId && workflowInstanceId > 0 ? workflowInstanceId : null;
  const canUseProjectFolderPicker =
    canManageProjectFileFolders && Boolean(projectFolderPickerWorkflowInstanceId);
  const showProjectFileUpload =
    !isTemplateChangeMode &&
    (!isEditMode || Boolean(projectFolderPickerWorkflowInstanceId));
  const canShowUploadPanel = showProjectFileUpload;
  const projectRootFolderLabel = isProjectFolderPreviewLoading
    ? t("workflows:createProjectModal.fileUpload.projectCodeCalculating")
    : projectDefaultFolderPathEntries?.[0]?.name ||
      projectFolderPreviewName ||
      projectFilesRootLabel;
  const selectedProjectRelativePath = (projectDefaultFolderPathEntries || [])
    .slice(1)
    .map((entry) => entry.name)
    .filter(Boolean)
    .join("/");
  const selectedProjectFolderDisplayLabel = selectedProjectRelativePath
    ? `${projectRootFolderLabel} / ${selectedProjectRelativePath
        .split("/")
        .filter(Boolean)
        .join(" / ")}`
    : projectRootFolderLabel;
  const projectSubfolderPathLabels: Array<{ relativePath: string; label: string }> =
    isProjectFolderPreviewLoading
      ? []
      : (() => {
          const paths = (virtualProjectFolderPaths || []).filter(
            (segments) => Array.isArray(segments) && segments.length > 0,
          );
          if (paths.length === 0) {
            return [];
          }
          const root = projectRootFolderLabel;
          return [...paths]
            .sort((a, b) => {
              const aPath = a.join("/").toLocaleLowerCase("tr-TR");
              const bPath = b.join("/").toLocaleLowerCase("tr-TR");
              return aPath.localeCompare(bPath, "tr", { sensitivity: "base" });
            })
            .map((segments) => {
              const relativePath = segments.join("/");
              return {
                relativePath,
                label: `${root} / ${segments.join(" / ")}`,
              };
            });
        })();
  const projectFolderPathLabels: Array<{ relativePath: string; label: string }> = [
    { relativePath: "", label: projectRootFolderLabel },
    ...projectSubfolderPathLabels,
  ];

  const stepOneContent = isLoading ? (
    <div className="flex items-center justify-center py-12">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-500">{t("workflows:loadingFormData")}</p>
      </div>
    </div>
  ) : formElements.length === 0 ? (
    <div className="text-center py-12 text-gray-500">
      <div className="text-6xl mb-4">∅</div>
      <h3 className="text-xl font-medium mb-2">
        {t("workflows:formElementsNotFound")}
      </h3>
      <p className="text-sm">{t("workflows:noFormElementsDefined")}</p>
    </div>
  ) : (
    <div className="space-y-6">
      {/* Project Members Selection */}
      {members.length > 0 && (
        <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-purple-900 mb-3 flex items-center">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            {t("workflows:selectProjectMembers")}
            <span className="ml-2 text-xs font-normal text-purple-600">
              {t("workflows:selectAtLeastOneMemberNote")}
            </span>
          </h4>
          <p className="text-xs text-purple-700 mb-3">
            {t("workflows:onlySelectedMembersCanAdd")}
          </p>
          {isLoadingMembers ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {members.map((member) => {
                // Robust check for current user (handle string/number mismatch)
                const isCurrentUser =
                  String(member.userId) === String(user?.id);
                const isSelected = selectedMembers.some(
                  (id) => String(id) === String(member.userId),
                );

                return (
                  <label
                    key={member.userId}
                    className={`flex items-center p-3 rounded-md border-2 transition-all ${
                      isSelected
                        ? isCurrentUser
                          ? "bg-blue-50 border-blue-400"
                          : "bg-purple-100 border-purple-400"
                        : "bg-white border-gray-200 hover:border-purple-300"
                    } ${isCurrentUser ? "cursor-default" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={isCurrentUser}
                      onChange={(e) => {
                        if (isCurrentUser) return; // Prevent change for current user

                        if (e.target.checked) {
                          if (!isSelected) {
                            setSelectedMembers([
                              ...selectedMembers,
                              member.userId,
                            ]);
                          }
                        } else {
                          setSelectedMembers(
                            selectedMembers.filter(
                              (id) => String(id) !== String(member.userId),
                            ),
                          );
                        }
                      }}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        {member.username}
                        {isCurrentUser && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold">
                            Siz (Proje Sahibi)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {member.email}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          <div className="mt-3 text-xs text-purple-600">
            {t("workflows:selectedMemberCount", {
              count: selectedMembers.length,
            })}
          </div>
        </div>
      )}

      {formElementsForUser.map((element) => (
        <div key={element.id}>{renderFormField(element)}</div>
      ))}

      {showProjectFileUpload && (
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h4 className="text-sm font-semibold text-blue-900">
                {t("workflows:createProjectModal.fileUpload.addFilesTitle")}
              </h4>
              <p className="text-xs text-blue-700">
                {isEditMode
                  ? t("workflows:createProjectModal.fileUpload.addFilesSubtitleEdit")
                  : t("workflows:createProjectModal.fileUpload.addFilesSubtitleCreate")}
              </p>
            </div>
            {projectFiles.length > 0 && (
              <button
                type="button"
                onClick={clearProjectFiles}
                className="text-xs px-2 py-1 rounded border border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                {t("workflows:createProjectModal.fileUpload.clear")}
              </button>
            )}
          </div>
          {canManageProjectFileFolders &&
            (canUseProjectFolderPicker ? (
              <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-white px-4 py-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-800">
                    {t("workflows:createProjectModal.fileUpload.mainFolder")}
                  </p>
                  <p className="text-xs font-medium text-gray-800 truncate">
                    {(projectDefaultFolderPathEntries || [])
                      .map((entry) => entry.name)
                      .filter(Boolean)
                      .join(" / ")}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setProjectFolderSelectionFileId(null);
                      setShowProjectFolderSelectModal(true);
                    }}
                    className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
                  >
                    {t("workflows:createProjectModal.fileUpload.selectFolder")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedProjectFolderId(null);
                      setProjectDefaultFolderPath("");
                      setProjectDefaultFolderPathEntries(
                        buildRootFolderPathEntries(projectFilesRootLabel),
                      );
                    }}
                    className="px-3 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold transition-colors"
                  >
                    {t("workflows:createProjectModal.fileUpload.reset")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-3 rounded-xl border border-blue-200 bg-white px-4 py-3">
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-800">
                      {t("workflows:createProjectModal.fileUpload.folders")}
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        setProjectDefaultFolderEntries(
                          buildRootFolderPathEntries(projectFolderPreviewName),
                        )
                      }
                      className="px-3 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold transition-colors"
                      title={t("workflows:createProjectModal.fileUpload.backToRootFolder")}
                    >
                      {t("workflows:createProjectModal.fileUpload.reset")}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1">
                    {projectFolderPathLabels.map((item) => (
                      <button
                        key={`subfolder-path-${item.relativePath || "root"}`}
                        type="button"
                        onClick={() =>
                          handleVirtualProjectFolderPathSelect(
                            item.relativePath.split("/").filter(Boolean),
                          )
                        }
                        className={`rounded px-2 py-1 text-[11px] transition-colors ${
                          item.relativePath === selectedProjectRelativePath
                            ? "bg-blue-100 text-blue-800 font-semibold"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                  {projectSubfolderPathLabels.length === 0 && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t("workflows:createProjectModal.fileUpload.noSubfolders")}
                    </p>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {!showProjectSubfolderInput ? (
                    <button
                      type="button"
                      onClick={() => setShowProjectSubfolderInput(true)}
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700"
                    >
                      {t("workflows:createProjectModal.fileUpload.createSubfolder")}
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={pendingProjectSubfolderName}
                        onChange={(e) => setPendingProjectSubfolderName(e.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleCreateProjectSubfolder();
                          }
                        }}
                        placeholder={t("workflows:createProjectModal.fileUpload.subfolderName")}
                        className="w-56 rounded-lg border border-gray-300 px-3 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                      />
                      <button
                        type="button"
                        onClick={handleCreateProjectSubfolder}
                        disabled={!pendingProjectSubfolderName.trim()}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {t("workflows:createProjectModal.fileUpload.add")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPendingProjectSubfolderName("");
                          setShowProjectSubfolderInput(false);
                        }}
                        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        {t("workflows:createProjectModal.fileUpload.cancel")}
                      </button>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-[11px] text-blue-700">
                  {t("workflows:createProjectModal.fileUpload.newSubfolderHint")}
                </p>
                <p className="mt-1 text-[11px] text-blue-700">
                  {t("workflows:createProjectModal.fileUpload.selectedFolder", {
                    folder: selectedProjectFolderDisplayLabel,
                  })}
                </p>
              </div>
            ))}
          {canShowUploadPanel && (
            <>
              {canManageProjectFileFolders && (
                <div className="mb-2 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => projectFolderInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 hover:text-amber-800"
                  >
                    <FolderIcon className="h-4 w-4" />
                    {t("dashboard:uploadProjectFiles.folderFromComputer")}
                  </button>
                </div>
              )}
              <div
                onDragOver={handleProjectFileDragOver}
                onDragLeave={handleProjectFileDragLeave}
                onDrop={handleProjectFileDrop}
                className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
                  isProjectFileDragOver
                    ? "border-blue-500 bg-blue-100"
                    : "border-blue-300 bg-white"
                }`}
              >
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    handleProjectFilesChange(e.target.files);
                    e.target.value = "";
                  }}
                  className="block w-full text-xs text-gray-700
                    file:mr-3 file:py-1.5 file:px-3
                    file:rounded file:border-0 file:text-xs file:font-semibold
                    file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                />
                <p className="text-[11px] text-gray-500 mt-2">
                  {t("workflows:createProjectModal.fileUpload.dropOrChoose")}
                </p>
                {canManageProjectFileFolders && (
                  <p className="text-[11px] text-blue-700 mt-1">
                    {t("dashboard:uploadProjectFiles.filesUploadedToSelectedFolder")}
                    <span className="font-semibold">
                      {selectedProjectFolderDisplayLabel}
                    </span>
                    {t("dashboard:uploadProjectFiles.selectedComputerFolderHint")}
                    {canUseProjectFolderPicker &&
                      t("dashboard:uploadProjectFiles.perFileFolderHint")}
                  </p>
                )}
              </div>
              {canManageProjectFileFolders && (
                <input
                  ref={projectFolderInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={(e) => {
                    handleProjectFilesChange(e.target.files);
                    e.target.value = "";
                  }}
                  {...({ webkitdirectory: "", directory: "" } as any)}
                />
              )}
              {projectFiles.length > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="text-xs font-medium text-blue-800">
                    {t("dashboard:selectedFiles")} ({projectFiles.length})
                  </div>
                  {projectFiles.map((projectFile) => (
                    <div
                      key={projectFile.id}
                      className="text-xs bg-white border border-blue-100 rounded px-2 py-2 space-y-2"
                    >
                              <div className="flex items-center justify-between">
                        <span className="truncate mr-2">
                          {projectFile.file.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeProjectFile(projectFile.id)}
                          className="text-rose-600 hover:text-rose-700"
                        >
                          {t("common:remove")}
                        </button>
                      </div>
                      <div className="space-y-1">
                        {getLocalProjectFileDisplayPath(projectFile.file) !==
                          projectFile.file.name && (
                          <p className="truncate text-[11px] text-gray-400">
                            {getLocalProjectFileDisplayPath(projectFile.file)}
                          </p>
                        )}
                        <label className="block text-[11px] font-medium text-gray-700">
                          {t("dashboard:uploadProjectFiles.fileDescription")}
                        </label>
                        <input
                          type="text"
                          value={projectFile.description}
                          onChange={(e) =>
                            updateProjectFileDescription(
                              projectFile.id,
                              e.target.value,
                            )
                          }
                          placeholder={t("dashboard:uploadProjectFiles.addDescriptionOptional")}
                          className="w-full rounded border border-blue-200 px-2 py-1.5 text-xs text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      {canManageProjectFileFolders && (
                        <>
                          {canUseProjectFolderPicker ? (
                            <div className="flex items-center justify-between gap-2 rounded-lg border border-blue-200 bg-blue-50 px-2 py-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <FolderIcon className="h-4 w-4 text-blue-700 flex-shrink-0" />
                                <span className="text-[11px] text-blue-800 truncate">
                                  {getProjectFileTargetFolderDisplayPath(
                                    projectFile,
                                  )}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setProjectFolderSelectionFileId(projectFile.id);
                                    setShowProjectFolderSelectModal(true);
                                  }}
                                  className="px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-semibold transition-colors"
                                >
                                  {t("dashboard:uploadProjectFiles.changeFolder")}
                                </button>
                                {(projectFile.folderPathEntries || []).length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateProjectFileFolderPath(
                                        projectFile.id,
                                        projectDefaultFolderPath,
                                        selectedProjectFolderId,
                                        projectDefaultFolderPathEntries,
                                      )
                                    }
                                    className="px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-[11px] font-semibold transition-colors"
                                  >
                                    {t("dashboard:uploadProjectFiles.defaultFolder")}
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-2 py-2">
                              <FolderIcon className="h-4 w-4 text-blue-700 flex-shrink-0" />
                              <span className="text-[11px] text-blue-800 truncate">
                                {getProjectFileTargetFolderDisplayPath(
                                  projectFile,
                                )}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {canUseProjectFolderPicker && (
            <ProjectFolderSelectModal
              isOpen={showProjectFolderSelectModal}
              onClose={() => {
                setShowProjectFolderSelectModal(false);
                setProjectFolderSelectionFileId(null);
              }}
              workflowInstanceId={projectFolderPickerWorkflowInstanceId}
              initialFolderId={
                projectFolderSelectionFileId
                  ? (
                      projectFiles.find(
                        (file) => file.id === projectFolderSelectionFileId,
                      )?.folderId || null
                    )
                  : selectedProjectFolderId
              }
              initialPath={
                projectFolderSelectionFileId
                  ? (
                      projectFiles.find(
                        (file) => file.id === projectFolderSelectionFileId,
                      )?.folderPathEntries || buildRootFolderPathEntries(projectFilesRootLabel)
                    )
                  : projectDefaultFolderPathEntries
              }
              rootLabel={
                projectDefaultFolderPathEntries?.[0]?.name || projectFilesRootLabel
              }
              onSelect={(folderId, path) => {
                const relativePath = (path || [])
                  .slice(1)
                  .map((entry) => entry.name)
                  .filter(Boolean)
                  .join("/");

                if (projectFolderSelectionFileId) {
                  updateProjectFileFolderPath(
                    projectFolderSelectionFileId,
                    relativePath,
                    folderId,
                    path || buildRootFolderPathEntries(projectFilesRootLabel),
                  );
                  setProjectFolderSelectionFileId(null);
                } else {
                  setSelectedProjectFolderId(folderId);
                  setProjectDefaultFolderPath(relativePath);
                  setProjectDefaultFolderPathEntries(
                    path || buildRootFolderPathEntries(projectFilesRootLabel),
                  );
                }
              }}
            />
          )}
        </div>
      )}

    </div>
  );

  const stepTwoContent = (
    <div className="space-y-6">
      {isLoadingSelectionData ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Seçim verileri yükleniyor...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="text-sm font-semibold text-slate-900">
                  Aktivite Grubu Seçimi
                </h4>
                <p className="text-xs text-slate-500">
                  Seçilen grupların aktiviteleri proje ile birlikte başlar.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleViewWorkflow}
                  disabled={!workflowId}
                  className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-100 disabled:opacity-50"
                >
                  {t("workflows:viewWorkflow")}
                </button>
                <button
                  type="button"
                  onClick={handleSelectAllPaftas}
                  className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-100"
                >
                  Tümünü seç
                </button>
                <button
                  type="button"
                  onClick={handleClearPaftas}
                  className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-100"
                >
                  Temizle
                </button>
              </div>
            </div>
            {paftaGroups.length === 0 ? (
              <p className="text-sm text-slate-500 mt-3">
                Aktivite grubu bulunamadı.
              </p>
            ) : (
              <div className="mt-4 space-y-2">
                {paftaGroups.map((group) => {
                  const isSelected = selectedPaftaStartNodes.includes(
                    group.startNodeId,
                  );
                  const activityItems = group.activityIds.map((activityId) => ({
                    id: activityId,
                    title:
                      activityLookup.get(activityId)?.title || `#${activityId}`,
                  }));
                  return (
                    <label
                      key={group.startNodeId}
                      className={`flex items-center p-2 rounded-md border transition-colors ${
                        isSelected
                          ? "bg-blue-50 border-blue-200"
                          : "bg-white border-slate-200 hover:border-blue-200"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handlePaftaToggle(group.startNodeId)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                      />
                      <div className="ml-3 flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {group.label}
                        </div>
                        {group.description && (
                          <div className="text-xs text-slate-500 mt-1 break-words">
                            {group.description}
                          </div>
                        )}
                        <div className="text-xs text-slate-500">
                          {group.activityIds.length} aktivite
                        </div>
                        {activityItems.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {activityItems.map((item) => (
                              <span
                                key={item.id}
                                className="px-2 py-0.5 rounded bg-slate-200 text-slate-700 text-[11px]"
                              >
                                {item.title}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="mt-3 text-xs text-slate-500">
              Seçilen grup sayısı: {selectedPaftaStartNodes.length}
            </div>
          </div>

        </>
      )}
    </div>
  );

  const isEditStyle = isEditMode || isTemplateChangeMode;
  const headerBgClass = isTemplateChangeMode
    ? "bg-gradient-to-r from-indigo-50 to-blue-50"
    : isEditMode
      ? "bg-gradient-to-r from-amber-50 to-orange-50"
      : "";
  const headerIconClass = isTemplateChangeMode
    ? "text-indigo-600"
    : isEditMode
      ? "text-amber-600"
      : "text-blue-600";
  const submitButtonClass = isTemplateChangeMode
    ? "bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
    : isEditMode
      ? "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500"
      : "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500";
  const headerTitle = isTemplateChangeMode
    ? t("workflows:changeProjectType")
    : isEditMode
      ? t("workflows:editDetails")
      : t("projects:createProject");
  const submitLabel = isTemplateChangeMode
    ? t("workflows:changeProjectType")
    : isEditMode
      ? t("common:save")
      : t("projects:createProject");
  const submitInProgressLabel = isEditStyle
    ? t("common:saving")
    : t("workflows:starting");
  const ActionIcon = isTemplateChangeMode ? ArrowPathIcon : PlayIcon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b border-gray-200 ${headerBgClass}`}
        >
          <div className="flex items-center space-x-3">
            <ActionIcon className={`h-6 w-6 ${headerIconClass}`} />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {headerTitle}
              </h3>
              <p className="text-xs text-gray-500">Adim {currentStep}/2</p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {currentStep === 1 ? stepOneContent : stepTwoContent}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          {currentStep === 2 && (
            <button
              type="button"
              onClick={() => setCurrentStep(1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              {t("common:back")}
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            {t("common:cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              isLoading ||
              isSubmitting ||
                formElements.length === 0 ||
              (currentStep === 2 && isLoadingSelectionData)
            }
            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${submitButtonClass}`}
          >
            <ActionIcon className="h-4 w-4" />
            <span>
              {isSubmitting
                ? submitInProgressLabel
                : currentStep === 1
                  ? t("common:next")
                  : submitLabel}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};
